import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Plus, X, Sparkles, FileText, Send, Save, Eye, Check,
  ChevronDown, ChevronRight, Loader2, DollarSign, Trash2, Info, GripVertical, Star
} from "lucide-react";
import EHIProgramsPanel from "@/components/EHIProgramsPanel";
// Iter 188.46 · Phase 3 — Proposal Builder (rich-text sections +
// paste-from-Word sanitize + save/reuse templates). Sits above the
// Line items panel on the New/Edit Quote page.
import { ProposalBuilderPanel } from "@/components/proposals/ProposalBuilderPanel";
import {
  CLEARINGHOUSE_TRIGGER_NAMES as CLEARINGHOUSE_TRIGGER_NAMES_HELPER,
  syncPercentageDescription,
  syncDollarDescription,
  reorderLineItem as reorderLineItemHelper,
  flatMonthlyRecurringTotal,
  isQuoteSendDisabled,
  getDiscountStatus,
  computeDiscountReduction,
  totalDiscountReduction,
} from "@/lib/quoteBuilderHelpers";
// Iter 333 (Option A) — pure pricing engine. Replaces the ad-hoc
// `sum(item.total)` monthly-only math with a 4-bucket routing
// (fixedMonthly / volumeMonthly / annual / oneTime) + rate lines.
import { totals as engineTotals, trueMRR as engineTrueMRR, hasRateLines as engineHasRateLines, summarySentence as engineSummarySentence, inferBasis as engineInferBasis } from "@/lib/quotePricingEngine";
// Iter 188.44 · Phase 2-A — seat-nesting + type-aware qty helpers.
// Pure functions; no side effects. See lib/quoteSeatNesting.js for
// docstrings + the 26-case pytest-parity Jest coverage.
import {
  ensureLineIds,
  getBases,
  appendWithAutoAttach,
  buildRenderList,
  removeWithCascade,
  qtyControlKind,
} from "@/lib/quoteSeatNesting";
import QuotePdfPreview from "@/components/quotes/QuotePdfPreview";
import { QuoteRichTextField } from "@/components/quotes/QuoteRichTextField";
import SearchableEntityPicker from "@/components/SearchableEntityPicker";
import { RecipientTypeToggle, EhiClientPicker, EhiClientFields } from "@/components/quotes/EhiClientRecipient";
import useConfirm from "@/hooks/useConfirm";
// Iter 188.54 — @dnd-kit sortable for the "lift-and-drag" reorder UX
// on both line items and (via ProposalBuilderPanel) proposal sections.
// Whole-card grab, animated re-layout, keyboard-accessible, and safe
// around form controls thanks to a target-aware pointerdown guard.
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMBLEM_URL = "https://customer-assets.emergentagent.com/job_285b778d-268b-4f9f-806e-ab953b551b56/artifacts/8tp297eu_Enable%20Healthcare%20emblem_BLUE.png";

// Iter 171 — Categories realigned to the EHI Product Catalog spec
// (5 spec categories: Application / Software, Ancillary Products,
// Service, Care Coordination Service, Care Coordination Application).
// `Implementation` and `Discounts` are operational categories that
// don't map to the spec but are kept because the quote builder uses them.
// Iter 333 (Option A — Correctness First) — every entry now carries
// `basis` (billing cadence: fixed/annual/onetime/perunit/pmpm/pctcoll/pctreimb)
// and `kind` (product shape: base/seat/feature/license/program).
//
// These two fields are load-bearing: the pricing engine reads `basis` to
// decide which of the four total buckets a line lands in, and the UI
// reads `kind` to pick the qty control (base=static-1, seat=stepper, etc.).
//
// Per spec §4 — CCM/APCM/AWV percentages + BOTH RPM percentages are
// `pctreimb` (% of reimbursement), NOT `pctcoll` — different approval
// floors and different print language. Only Pro Bundle (6%),
// RevQ AI Billing (2%), and Revenue Cycle Service (4%) are `pctcoll`.
const PRODUCTS_CATALOG = [
  // ── CATEGORY 1 — APPLICATION / SOFTWARE
  { name: "EHR Essential", description: "AI-powered charting, Scheduler, eRx, eLabs, Patient Portal, Telemedicine", price: 250, unit: "/mo", model: "fixed", basis: "fixed", kind: "base", category: "Application / Software" },
  { name: "Essentials Bundle (EHR + PMS)", description: "Pro EHR + Practice Management System", price: 500, unit: "/mo", model: "fixed", basis: "fixed", kind: "base", category: "Application / Software" },
  { name: "Pro Bundle (EHR + RCM)", description: "Pro EHR + Revenue Cycle Management @ 6% monthly collections", price: 6, unit: "% monthly", model: "percentage", basis: "pctcoll", kind: "base", category: "Application / Software" },
  { name: "RevQ Billing App", description: "Integrated clearinghouse, reporting, scheduling, claims tracker", price: 250, unit: "/mo", model: "fixed", basis: "fixed", kind: "base", category: "Application / Software" },

  // ── CATEGORY 3 — SERVICE
  { name: "RevQ (AI Billing)", description: "AI billing with auto claims, posting, payor rules @ 2% monthly collections", price: 2, unit: "% monthly", model: "percentage", basis: "pctcoll", kind: "license", category: "Service" },
  { name: "Revenue Cycle Service", description: "End-to-end claims mgmt, dedicated account mgr @ 4% monthly", price: 4, unit: "% monthly", model: "percentage", basis: "pctcoll", kind: "license", category: "Service" },

  // ── CATEGORY 4 — CARE COORDINATION SERVICE — CCM (Chronic Care Management)
  // pctreimb per spec §4 (retagged from % collections — different approval floor)
  { name: "CCM — Percentage", description: "50% of payer reimbursement per patient per month", price: 50, unit: "% reimbursement", model: "percentage", basis: "pctreimb", kind: "program", category: "Care Coordination Service" },
  { name: "CCM 99490", description: "Non-complex CCM, first 20 min", price: 28, unit: "/patient/mo", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "CCM 99439", description: "Each additional 20 min (add-on to 99490)", price: 20, unit: "/patient/mo", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },

  // ── CATEGORY 4 — CARE COORDINATION SERVICE — RPM (Remote Patient Monitoring)
  { name: "RPM — Percentage (recouped)", description: "100% of reimbursement months 1-2 (covers device) then 50% from month 3", price: 50, unit: "% reimbursement", model: "percentage", basis: "pctreimb", kind: "program", category: "Care Coordination Service" },
  { name: "RPM — Percentage (device upfront)", description: "50% of reimbursement + $100 upfront per device", price: 50, unit: "% reimbursement", model: "percentage", basis: "pctreimb", kind: "program", category: "Care Coordination Service" },
  { name: "RPM 99454 (Data Transmission)", description: "2-30 readings per 30 days", price: 23, unit: "/patient/mo", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "RPM 99470 (Care Coordination 0-10 min)", description: "Care coordination, 0-10 minutes", price: 13, unit: "/patient/mo", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "RPM 99457 (Care Coordination 10-20 min)", description: "Care coordination, 10-20 minutes", price: 26, unit: "/patient/mo", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "RPM 99458 (Additional 20 min)", description: "Each additional 20 minutes", price: 27, unit: "/patient/mo", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },

  // ── CATEGORY 4 — CARE COORDINATION SERVICE — APCM (Advanced Primary Care Management)
  { name: "APCM — Percentage", description: "50% of payer reimbursement per patient per month", price: 50, unit: "% reimbursement", model: "percentage", basis: "pctreimb", kind: "program", category: "Care Coordination Service" },
  { name: "APCM G0557", description: "APCM services per patient per month (standard)", price: 21, unit: "/patient/mo", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "APCM G0558", description: "APCM services per patient per month (Qualified Medicare Beneficiaries)", price: 58, unit: "/patient/mo", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },

  // ── CATEGORY 4 — CARE COORDINATION SERVICE — AWV (Annual Wellness Visit)
  { name: "AWV — Percentage", description: "50% of payer reimbursement per patient", price: 50, unit: "% reimbursement", model: "percentage", basis: "pctreimb", kind: "program", category: "Care Coordination Service" },
  { name: "AWV G0438 (Initial AWV)", description: "Initial Annual Wellness Visit", price: 88, unit: "/patient", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "AWV G0439 (Subsequent AWV)", description: "Subsequent Annual Wellness Visit", price: 72, unit: "/patient", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "AWV G0444 (Depression Screening)", description: "Depression screening per patient", price: 10, unit: "/patient", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "AWV G0442 (Alcohol Screening)", description: "Alcohol screening per patient", price: 10, unit: "/patient", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },
  { name: "AWV G0136 (Social Determinants of Health)", description: "Social determinants of health screening per patient", price: 10, unit: "/patient", model: "per_patient", basis: "pmpm", kind: "program", category: "Care Coordination Service" },

  // ── CATEGORY 2 — ANCILLARY PRODUCTS
  // NOTE: RPM Device is a per-device ONE-TIME charge, not recurring monthly.
  { name: "RPM Device (Upfront)", description: "GSM-enabled RPM device, one-time per device charge", price: 100, unit: "/device one-time", model: "per_patient", basis: "onetime", kind: "feature", category: "Ancillary Products" },
  { name: "Additional Provider", description: "Extra provider user license", price: 150, unit: "/mo", model: "fixed", basis: "fixed", kind: "seat", category: "Ancillary Products" },
  { name: "Prescriber License", description: "Prescriber user access", price: 45, unit: "/mo", model: "fixed", basis: "fixed", kind: "seat", category: "Ancillary Products" },
  { name: "Prescribing Agent", description: "Prescribing agent user license", price: 25, unit: "/mo", model: "fixed", basis: "fixed", kind: "seat", category: "Ancillary Products" },
  { name: "Non-Medical User", description: "Non-medical staff user license", price: 50, unit: "/mo", model: "fixed", basis: "fixed", kind: "seat", category: "Ancillary Products" },
  { name: "Account Manager", description: "Dedicated account manager", price: 100, unit: "/mo", model: "fixed", basis: "fixed", kind: "feature", category: "Ancillary Products" },
  // EPCS and PDMP are ANNUAL not monthly — used to get 12× inflated pre-Iter-333.
  { name: "EPCS", description: "Electronic Prescribing of Controlled Substances", price: 250, unit: "/yr per MD", model: "fixed", basis: "annual", kind: "feature", category: "Ancillary Products" },
  { name: "PDMP (Prescription Drug Monitoring)", description: "Prescription Drug Monitoring Program integration", price: 250, unit: "/yr per MD", model: "fixed", basis: "annual", kind: "feature", category: "Ancillary Products" },
  { name: "RingCentral Fax Integration", description: "RingCentral fax integration for the EHR", price: 75, unit: "/mo", model: "fixed", basis: "fixed", kind: "feature", category: "Ancillary Products" },
  // Direct Messaging is $250/yr — also used to be 12× inflated pre-Iter-333.
  { name: "Direct Messaging", description: "Secure Direct messaging add-on", price: 250, unit: "/yr", model: "fixed", basis: "annual", kind: "feature", category: "Ancillary Products" },
  { name: "Clearinghouse Fees", description: "Claims, ERA, Eligibility transactions @ $0.15 each", price: 0.15, unit: "/transaction", model: "per_patient", basis: "perunit", kind: "feature", category: "Ancillary Products" },
  { name: "Prior Authorization", description: "Per-authorization fee for prior auth add-on", price: 15, unit: "/authorization", model: "per_patient", basis: "perunit", kind: "feature", category: "Ancillary Products" },
  { name: "Benefits Check", description: "Per-benefits-check fee", price: 6, unit: "/check", model: "per_patient", basis: "perunit", kind: "feature", category: "Ancillary Products" },
  { name: "EHR Integration Services", description: "Connect EHI care-coordination/billing/telehealth to a third-party EHR (Epic, Athena, eCW, etc.). Custom scope — contact EHI for pricing.", price: 0, unit: "custom", model: "fixed", basis: "fixed", kind: "feature", category: "Ancillary Products" },
  // ── Implementation / Setup Fees — added Iter 140.3 (May 2026)
  // Manual line-item reps pick when scoping care-coordination services.
  // Price defaults to $0 — rep fills in based on the scope of work.
  { name: "Implementation / Setup Fee", description: "One-time implementation & setup (care coordination onboarding, training, workflow configuration)", price: 0, unit: "one-time", model: "fixed", basis: "onetime", kind: null, category: "Implementation" },
  // ── Discounts (Iter 140.6) ─────────────────────────────────────
  // Two discount line types with their own UI:
  //   - Setup Fee Discount: % or $ off the Implementation / Setup Fee
  //   - Product Discount:   % or $ off a specific quoted product,
  //                         with an expiration date. After expiry the
  //                         discount auto-deactivates (greyed out) and
  //                         the quote total snaps back to full price.
  //                         7-day warning badge appears before expiry.
  { name: "Setup Fee Discount", description: "Discount off the Implementation / Setup Fee", price: 0, unit: "discount", model: "setup_discount", category: "Discounts" },
  { name: "Product Discount", description: "Time-limited discount applied to a specific product line", price: 0, unit: "discount", model: "product_discount", category: "Discounts" },
];

// Iter 140.3 — when a rep adds PMS or RCM bundles, also auto-add a
// Clearinghouse Fees line item (qty 1, rep adjusts manually). Keyed by
// product NAME so this stays decoupled from the catalog ordering.
// Imported from `lib/quoteBuilderHelpers.js` for shared unit-test
// coverage; re-aliased here for readability inside this component.
const CLEARINGHOUSE_TRIGGER_NAMES = CLEARINGHOUSE_TRIGGER_NAMES_HELPER;

// Iter 140.6 — CATEGORIES is now derived inside the component from
// `ACTIVE_CATALOG` (DB-backed + in-code fallback). Keep the in-code
// PRODUCTS_CATALOG as the seed/fallback source.

const MODEL_PILLS = {
  fixed: { label: "Fixed", bg: "var(--blue-light)", color: "var(--blue)" },
  percentage: { label: "% of collections", bg: "#f3e8ff", color: "#7c3aed" },
  per_patient: { label: "Per patient", bg: "#e0f7f0", color: "#0d9488" },
};

// Iter 188.54 — Sortable row wrapper. Spreads sortable listeners on
// the whole card so users can grab-and-drag from anywhere on the row.
// A capture-phase pointerdown guard suppresses drag intent when the
// event originates inside a form control (input/select/textarea/
// button/label) or an element flagged with `data-no-drag`, keeping
// every existing interaction (qty stepper, price edit, remove button,
// "Attach to" select, etc.) working exactly as before.
function SortableItemBox({ id, children, className, style: extraStyle, testid, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  // Guarded listeners: if the pointerdown originated inside a form
  // control we bail without invoking the sortable listener. This lets
  // clicks focus inputs / toggle selects / press buttons normally.
  const guardedListeners = {
    ...listeners,
    onPointerDown: (event) => {
      const t = event.target;
      if (t && t.closest && t.closest('input, select, textarea, button, label, [data-no-drag]')) {
        return;
      }
      listeners?.onPointerDown?.(event);
    },
    onKeyDown: (event) => {
      const t = event.target;
      if (t && t.closest && t.closest('input, select, textarea, [data-no-drag]')) {
        return;
      }
      listeners?.onKeyDown?.(event);
    },
  };

  const composedStyle = {
    ...extraStyle,
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 180ms cubic-bezier(0.2, 0, 0, 1)",
    zIndex: isDragging ? 40 : (extraStyle?.zIndex ?? "auto"),
    position: isDragging ? "relative" : (extraStyle?.position ?? undefined),
    boxShadow: isDragging
      ? "0 18px 40px rgba(15, 41, 66, 0.22), 0 2px 6px rgba(15, 41, 66, 0.12)"
      : (extraStyle?.boxShadow || undefined),
    background: isDragging ? "#ffffff" : (extraStyle?.background || undefined),
    borderRadius: isDragging ? 10 : (extraStyle?.borderRadius || undefined),
    cursor: isDragging ? "grabbing" : (extraStyle?.cursor || "grab"),
    opacity: isDragging ? 0.98 : (extraStyle?.opacity ?? 1),
    touchAction: "manipulation",
  };

  return (
    <div
      ref={setNodeRef}
      style={composedStyle}
      className={className}
      data-testid={testid}
      data-dragging={isDragging ? "true" : undefined}
      {...attributes}
      {...guardedListeners}
    >
      {children}
    </div>
  );
}

/* ─── "What's Next?" post-save screen ─── */
function PostSaveScreen({ quote, contact, onGoBack, onSendEmail, onSendSMS }) {
  // Iter 140.3 — approval gating. Pending-approval quotes are NOT
  // ready to send; reps must wait for an approver to flip the status
  // to "approved" before they can email/SMS the contact. All other
  // statuses (draft, approved, sent) keep the original send flow.
  const status = (quote.status || "draft").toLowerCase();
  const isPending = status === "pending_approval" || status === "pending";
  const sendDisabled = isQuoteSendDisabled(status);
  const titleText = isPending
    ? `${quote.quote_number} submitted for approval`
    : `${quote.quote_number} is ready to send`;
  const subtitleText = isPending
    ? "Sending unlocks once an approver reviews and approves this quote."
    : "How would you like to send it?";

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 120px)" }}>
      <div style={{ width: 540, textAlign: "center" }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: isPending ? "var(--warning-soft)" : "var(--green-light)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Check style={{ width: 24, height: 24, color: isPending ? "#a06200" : "var(--green)" }} />
        </div>
        <div data-testid="post-save-title" style={{ fontSize: 20, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>
          {titleText}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginBottom: 24 }}>
          {quote.title} &middot; ${(quote.total || 0).toLocaleString()}/mo
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16, color: "var(--text-secondary)" }}>
          {subtitleText}
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 24 }}>
          <button
            onClick={sendDisabled ? undefined : onSendEmail}
            disabled={sendDisabled}
            className="btn"
            style={{
              padding: "16px 24px", borderRadius: 12, border: "0.5px solid var(--border)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 140,
              cursor: sendDisabled ? "not-allowed" : "pointer",
              opacity: sendDisabled ? 0.45 : 1,
            }}
            data-testid="post-save-email"
            title={sendDisabled ? "Waiting on approver" : ""}
          >
            <Send style={{ width: 18, height: 18, color: "var(--blue)" }} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>Send via Email</span>
            <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{contact?.email || "No email"}</span>
            <span style={{ fontSize: 9, color: sendDisabled ? "var(--text-tertiary)" : "var(--blue)", fontWeight: 600 }}>
              {sendDisabled ? "Locked — pending approval" : "Draft ready"}
            </span>
          </button>
          <button
            onClick={sendDisabled ? undefined : onSendSMS}
            disabled={sendDisabled}
            className="btn"
            style={{
              padding: "16px 24px", borderRadius: 12, border: "0.5px solid var(--border)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 140,
              cursor: sendDisabled ? "not-allowed" : "pointer",
              opacity: sendDisabled ? 0.45 : 1,
            }}
            data-testid="post-save-sms"
            title={sendDisabled ? "Waiting on approver" : ""}
          >
            <FileText style={{ width: 18, height: 18, color: "var(--blue)" }} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>Send via SMS</span>
            <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{contact?.phone || "No phone"}</span>
            <span style={{ fontSize: 9, color: sendDisabled ? "var(--text-tertiary)" : "var(--blue)", fontWeight: 600 }}>
              {sendDisabled ? "Locked — pending approval" : "Draft ready"}
            </span>
          </button>
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button onClick={onGoBack} className="btn" style={{ fontSize: 12 }}>Back to lead</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Live Preview Panel ─── */
function LivePreview({ title, quoteNumber, contact, lineItems, total, notes, terms, validUntil, pricingBuckets, trueMonthlyMRR }) {
  // Iter 333 (Option A) — the miniature preview also honours the 4
  // buckets so the mini-card matches the right-panel authoritative
  // numbers. The `total` prop is kept for BC but ignored when
  // pricingBuckets is available.
  const buckets = pricingBuckets || null;
  return (
    <div style={{ background: "#fff", borderRadius: 8, border: "0.5px solid var(--border)", overflow: "hidden", fontSize: 8, transform: "scale(1)", transformOrigin: "top left" }}>
      {/* Navy header */}
      <div style={{ background: "#1b365D", padding: "10px 12px", color: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>ENABLE HEALTHCARE</div>
            <div style={{ fontSize: 7, opacity: 0.7 }}>AI-Powered Healthcare Solutions</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 8, opacity: 0.7 }}>QUOTE</div>
            <div style={{ fontSize: 9, fontWeight: 700 }}>{quoteNumber || "QT-XXXX"}</div>
          </div>
        </div>
      </div>
      <div style={{ padding: "8px 12px" }}>
        <div style={{ fontSize: 8, color: "#666", marginBottom: 2 }}>Prepared for</div>
        <div style={{ fontSize: 9, fontWeight: 600 }}>{contact?.name || "—"}</div>
        <div style={{ fontSize: 7, color: "#999" }}>{contact?.company || ""}</div>
        {validUntil && <div style={{ fontSize: 7, color: "#999", marginTop: 4 }}>Valid until: {new Date(validUntil).toLocaleDateString()}</div>}
        {/* Line items */}
        <div style={{ marginTop: 8, borderTop: "0.5px solid #eee", paddingTop: 6 }}>
          <div style={{ fontSize: 7, fontWeight: 600, color: "#999", marginBottom: 3 }}>LINE ITEMS</div>
          {lineItems.slice(0, 5).map((item, i) => {
            const basis = item.basis || "fixed";
            const isRate = basis === "pctcoll" || basis === "pctreimb";
            const amount = isRate
              ? `${parseFloat(item.unit_price) || 0}%`
              : `$${(parseFloat(item.total) || 0).toLocaleString()}`;
            const suffix = isRate ? "" : (basis === "annual" ? "/yr" : basis === "onetime" ? " one-time" : "/mo");
            return (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 8, padding: "2px 0", borderBottom: "0.5px solid #f5f5f5" }}>
                <span style={{ fontWeight: 500 }}>{item.product || "—"}</span>
                <span style={{ fontWeight: 600, color: isRate ? "#6d40c8" : "inherit" }}>{amount}{suffix}</span>
              </div>
            );
          })}
          {lineItems.length > 5 && <div style={{ fontSize: 7, color: "#999" }}>+{lineItems.length - 5} more items</div>}
        </div>
        {/* Buckets */}
        {buckets ? (
          <div style={{ marginTop: 6, paddingTop: 4, borderTop: "1px solid #1b365D", fontSize: 8, display: "flex", flexDirection: "column", gap: 2 }}>
            {buckets.fixedMonthly > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
                <span>Fixed monthly</span><span>${buckets.fixedMonthly.toLocaleString()}/mo</span>
              </div>
            )}
            {buckets.volumeMonthly > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", color: "#475569" }}>
                <span>Volume-based</span><span>${buckets.volumeMonthly.toLocaleString(undefined, { maximumFractionDigits: 2 })}/mo</span>
              </div>
            )}
            {buckets.annual > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", color: "#475569" }}>
                <span>Billed annually</span><span>${buckets.annual.toLocaleString()}/yr</span>
              </div>
            )}
            {buckets.oneTime > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", color: "#475569" }}>
                <span>One-time</span><span>${buckets.oneTime.toLocaleString()}</span>
              </div>
            )}
            {buckets.rateLines && buckets.rateLines.length > 0 && (
              <div style={{ marginTop: 3, fontSize: 7, color: "#6d40c8", fontStyle: "italic" }}>
                Plus {buckets.rateLines.map(r => `${r.rate}% of ${r.of}`).join(", ")}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, paddingTop: 4, borderTop: "1px solid #1b365D", fontSize: 10, fontWeight: 700 }}>
            <span>Total</span>
            <span>${(total || 0).toLocaleString()}</span>
          </div>
        )}
        {notes && (() => { const plain = (new DOMParser().parseFromString(notes.replace(/<[^>]+>/g, " "), "text/html").body.textContent || "").replace(/\s+/g, " ").trim(); return plain ? <div style={{ marginTop: 6, fontSize: 7, color: "#666", background: "#f8fafc", padding: 4, borderRadius: 3 }}>{plain.slice(0, 100)}{plain.length > 100 ? "..." : ""}</div> : null; })()}
      </div>
    </div>
  );
}

const isTermsHeading = (n) => n && n.nodeType === 1 && /^H[1-6]$/.test(n.tagName) && /Terms:$/.test((n.textContent || "").trim());

// Locate the "{cat} Terms:" heading inside terms HTML (null if absent).
const findTermsHeading = (html, cat) => {
  const box = document.createElement("div");
  box.innerHTML = html || "";
  const h = Array.from(box.querySelectorAll("h1,h2,h3,h4,h5,h6")).find(el => (el.textContent || "").trim() === `${cat} Terms:`);
  return h ? { box, h } : null;
};

// Remove a category's section: its heading up to the next "... Terms:" heading.
const removeTermsSection = (html, cat) => {
  const found = findTermsHeading(html, cat);
  if (!found) return html;
  const { box } = found;
  let start = found.h;
  while (start.parentNode && start.parentNode !== box) start = start.parentNode;
  const isBr = (n) => n && n.nodeType === 1 && n.tagName === "BR";
  const isBlank = (n) => n && n.nodeType === 3 && !n.textContent.trim();
  let prevSib = start.previousSibling;
  while (isBlank(prevSib)) prevSib = prevSib.previousSibling;
  let n = start;
  while (n) {
    const next = n.nextSibling;
    box.removeChild(n);
    n = next;
    if (n && (isTermsHeading(n) || (n.nodeType === 1 && n.querySelector && Array.from(n.querySelectorAll("h1,h2,h3,h4,h5,h6")).some(isTermsHeading)))) break;
  }
  if (!n && isBr(prevSib)) box.removeChild(prevSib);
  else { while (box.firstChild && (isBr(box.firstChild) || isBlank(box.firstChild))) box.removeChild(box.firstChild); }
  return box.innerHTML;
};

const DEFAULT_TERMS = "Terms and Conditions included in the Service Agreements. Monthly Recurring Charges will be billed upon the Go Live Date set forth in the Implementation Deliverables Checklist. If the Client terminates service prior to the Service End Date or before Service Start Date, Client shall immediately pay EHI any implementation, setup, or training fees that were waived or discounted.";

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const isEdit = !!id;
  // Iter 334 — Back button returns to the list view (`/quotes`), which
  // is now the sidebar's landing page for Quotes & Contracts. Preserves
  // any explicit returnTo passed via location state (e.g., from a lead
  // record's "Create quote" launcher, once B/C ships).
  const returnTo = location.state?.returnTo || "/quotes";

  // Form state
  const [title, setTitle] = useState("");
  const [contactId, setContactId] = useState("");
  const [leadId, setLeadId] = useState("");
  const [recipientType, setRecipientType] = useState("lead");
  const [ehiClient, setEhiClient] = useState(null);
  const confirmDialog = useConfirm();
  const [lineItems, setLineItems] = useState([]);
  // Iter 188.46 · Phase 3 — Proposal Builder state. Rich-text sections
  // live in `proposalSections` (persisted as `proposal_sections` on
  // the quote); the "Include" toggle flips whether the PDF renders
  // as a proposal document vs a plain quote.
  const [proposalIncluded, setProposalIncluded] = useState(false);
  const [proposalSections, setProposalSections] = useState([]);
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  // Category-driven default Terms. `categoryTerms` maps category name → HTML.
  // `userHasEditedTerms` guards auto-population from clobbering manual edits.
  const [categoryTerms, setCategoryTerms] = useState({});
  const [userHasEditedTerms, setUserHasEditedTerms] = useState(false);
  const [termsAutoApplied, setTermsAutoApplied] = useState(false);
  const [validUntil, setValidUntil] = useState("");
  const [noExpiry, setNoExpiry] = useState(false);
  const [collectionsEstimate, setCollectionsEstimate] = useState(0);
  const [saving, setSaving] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  // Iter 140.6 — catalog is now sourced from `/api/quote-catalog/products`
  // (seeded from the in-code PRODUCTS_CATALOG above). On API failure we
  // fall back to the in-code constant so the builder never blanks the
  // catalog. Super_admin + contracts_agent can edit catalog entries
  // live via the Quotes > Catalog tab.
  //
  // Iter 168.f (Peter — issue 5fe019b1 "blank New Quote page") — the
  // earlier order of declarations referenced ACTIVE_CATALOG ABOVE its
  // own `const` definition. Even though `const` is hoisted, it's in the
  // temporal dead zone until its declaration line executes, so the
  // useMemo+useState above threw `ReferenceError: Cannot access
  // 'ACTIVE_CATALOG' before initialization` on every mount and the
  // whole /quotes/new route blanked out. Moved liveCatalog +
  // ACTIVE_CATALOG up here so the downstream hooks can use them safely.
  const [liveCatalog, setLiveCatalog] = useState(null);
  const ACTIVE_CATALOG = liveCatalog && liveCatalog.length > 0 ? liveCatalog : PRODUCTS_CATALOG;

  // ── Category default Terms & Conditions (auto-population) ──────────────
  // Fetch admin-configured per-category terms once; QuoteBuilder then keeps
  // the Terms field in sync with the categories of the selected line items.
  const [categoryTermsLoaded, setCategoryTermsLoaded] = useState(false);
  // Categories seen on the previous line-item pass (null until an edited quote loads).
  const prevTermCatsRef = useRef(isEdit ? null : new Set());
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/quote-catalog/category-terms`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const map = {};
        (r.data?.items || []).forEach(x => { if (x.category) map[x.category] = x.terms_html || ""; });
        setCategoryTerms(map);
      })
      .catch(() => {})
      .finally(() => setCategoryTermsLoaded(true));
  }, [token]);

  const lineItemCategories = (items, catalog = ACTIVE_CATALOG) => {
    const nameToCat = {};
    (catalog || []).forEach(p => { if (p?.name) nameToCat[p.name] = p.category; });
    const cats = [];
    (items || []).forEach(it => {
      const cat = it.category || nameToCat[it.product];
      if (cat && !cats.includes(cat)) cats.push(cat);
    });
    return cats;
  };

  const termsSection = (c) => `<h3>${c} Terms:</h3>${categoryTerms[c]}`;

  // Concatenate the terms of every category present in `items`, alphabetical,
  // each under an "<h3>{Category} Terms:</h3>" heading. "" if none configured.
  const buildCombinedTerms = (items) => {
    const withTerms = lineItemCategories(items).filter(c => (categoryTerms[c] || "").trim()).sort((a, b) => a.localeCompare(b));
    if (!withTerms.length) return "";
    return withTerms.map(termsSection).join("<br/>");
  };

  // Keep the Terms field in sync with line-item categories.
  useEffect(() => {
    if (!categoryTermsLoaded || !prevTermCatsRef.current) return;
    const prev = prevTermCatsRef.current;
    const cur = lineItemCategories(lineItems);
    prevTermCatsRef.current = new Set(cur);
    if (!isEdit && !userHasEditedTerms) {
      const combined = buildCombinedTerms(lineItems);
      setTerms(combined || DEFAULT_TERMS);
      setTermsAutoApplied(!!combined);
      return;
    }
    const removed = [...prev].filter(c => !cur.includes(c));
    const added = cur.filter(c => !prev.has(c) && (categoryTerms[c] || "").trim());
    if (!removed.length && !added.length) return;
    let html = terms || "";
    const removedDone = removed.filter(c => {
      const next = removeTermsSection(html, c);
      const changed = next !== html;
      html = next;
      return changed;
    });
    const addedDone = added.filter(c => {
      if (findTermsHeading(html, c)) return false;
      html = (html.replace(/<br\s*\/?>\s*$/i, "").trim() ? html + "<br/>" : "") + termsSection(c);
      return true;
    });
    if (!removedDone.length && !addedDone.length) return;
    setTerms(html);
    if (addedDone.length) toast.success(`Added ${addedDone.join(", ")} terms`);
    if (removedDone.length) toast(`Removed ${removedDone.join(", ")} terms`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, categoryTerms, categoryTermsLoaded, isEdit, userHasEditedTerms]);

  // Manual edits flip the guard off so auto-population stops clobbering.
  const handleTermsChange = (v) => {
    setTerms(v);
    setUserHasEditedTerms(true);
    setTermsAutoApplied(false);
  };

  // "Reset to Category Defaults" — replace Terms with the current categories' defaults.
  const resetTermsToCategoryDefaults = () => {
    setUserHasEditedTerms(false);
    const combined = buildCombinedTerms(lineItems);
    setTerms(combined || DEFAULT_TERMS);
    setTermsAutoApplied(!!combined);
  };

  // Iter 334 (Option B.1) — "Frequently quoted" pinned section at the
  // top of the catalog rail. Fetched from the backend based on the
  // user's last 30 days of quote history. Empty state falls back to
  // the mockup's canonical 5 defaults so brand-new reps still see a
  // useful pinned block on day-one.
  const FREQUENT_FALLBACK = [
    "EHR (MDNet) — Essential", "EHR Essential",           // live catalog uses "(MDNet) — Essential"; seed uses "Essential"
    "Essentials Bundle (EHR + PMS)",
    "Pro Bundle (EHR + RCM)",
    "RevQ Billing App",
    "Additional Provider",
  ];
  const [frequentlyQuoted, setFrequentlyQuoted] = useState({ items: [], loaded: false });

  // Iter 140.6 — merge category set from BOTH static seed + live DB
  // catalog so newly-added categories from the Catalog tab show up
  // without a deploy.
  // Iter 171 — canonical category order matches the EHI Product
  // Catalog spec (Feb 2026). Any unknown categories are appended
  // at the end alphabetically so DB-added categories still appear.
  const CATEGORIES = useMemo(() => {
    const CANONICAL_ORDER = [
      "Application / Software",
      "Ancillary Products",
      "Service",
      "Care Coordination Service",
      "Care Coordination Application",
      "Implementation",
      "Discounts",
    ];
    const set = new Set(PRODUCTS_CATALOG.map(p => p.category));
    ACTIVE_CATALOG.forEach(p => { if (p.category) set.add(p.category); });
    const known = CANONICAL_ORDER.filter(c => set.has(c));
    const extras = Array.from(set).filter(c => !CANONICAL_ORDER.includes(c)).sort();
    return [...known, ...extras];
  }, [ACTIVE_CATALOG]);
  // Iter 334 (Option B.1) — Catalog groups are now COLLAPSED by default
  // (matches the mockup's `CATALOG · 42 SKUs, collapsed` behavior).
  // Rep's expand/collapse choices are persisted in localStorage so the
  // rail remembers state across sessions.
  const CATALOG_EXPANDED_KEY = "qb_catalog_expanded_v1";
  const [expandedCategories, setExpandedCategories] = useState(() => {
    try {
      const raw = localStorage.getItem(CATALOG_EXPANDED_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set();  // default: everything collapsed
  });
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteNumber, setQuoteNumber] = useState("");
  // Creator of the quote (for the "Prepared by" block). Null for new quotes,
  // where the current user IS the creator.
  const [quoteOwner, setQuoteOwner] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [postSaveQuote, setPostSaveQuote] = useState(null);
  // Recipient email stored on the quote itself — used as the final
  // fallback for AI Draft Email when the lead/contact record can't be
  // loaded (Jun 2026 — Hamza).
  const [quoteContactEmail, setQuoteContactEmail] = useState("");
  const [hoveredProduct, setHoveredProduct] = useState(null);
  // Section 11B — AI product suggestions
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null); // { suggestions:[{name,reason,tier}], rationale }
  // ✦ AI-drafted Quote Email (review-before-send)
  const [emailModal, setEmailModal] = useState(null); // { subject, body, to, sending, drafting, savedQuote }
  // EHI Pricing Rules — programs the user has added (Phase 2, May 2026)
  // value shape: [{program: "CCM"|"RPM"|"APCM", model, inputs}]
  const [ehiPrograms, setEhiPrograms] = useState([]);
  const [ehiComputed, setEhiComputed] = useState(null);

  const addEhiProgram = (programKey) => {
    setEhiPrograms(prev => {
      if (prev.some(p => p.program === programKey)) return prev;
      return [...prev, { program: programKey, model: "", inputs: {} }];
    });
  };

  // Iter 140.6 — catalog source notes moved up to top of component to
  // avoid temporal-dead-zone crash (Iter 168.f).

  // Load data
  useEffect(() => {
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [contactsRes, leadsRes, catalogRes] = await Promise.all([
          axios.get(`${API}/contacts`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/leads`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/quote-catalog/products`, { headers }).catch(() => ({ data: { items: [] } })),
        ]);
        setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : contactsRes.data?.contacts || []);
        setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
        // Catalog endpoint is gated to super_admin + contracts_agent;
        // other roles fall through to PRODUCTS_CATALOG via the empty
        // 200 / 403 fallback. Strip the soft-deleted/inactive rows
        // (the backend already does this but be defensive).
        const items = Array.isArray(catalogRes.data?.items) ? catalogRes.data.items : [];
        const activeItems = items.filter(p => p.active !== false);
        setLiveCatalog(activeItems);

        // Iter 334 (Option B.1) — fire-and-forget fetch for the pinned
        // "Frequently quoted" section. Failure is silent; we fall
        // through to the canonical FREQUENT_FALLBACK.
        axios.get(`${API}/quotes/frequently-quoted?days=30&limit=5`, { headers })
          .then(r => setFrequentlyQuoted({ items: r.data?.items || [], loaded: true }))
          .catch(() => setFrequentlyQuoted({ items: [], loaded: true }));

        if (isEdit) {
          const res = await axios.get(`${API}/quotes/${id}`, { headers });
          const q = res.data;
          setTitle(q.title || "");
          setContactId(q.contact_id || "");
          setLeadId(q.lead_id || "");
          if (q.recipient_type === "ehi_client" && q.ehi_client) { setRecipientType("ehi_client"); setEhiClient(q.ehi_client); }
          // Iter 333 (Option A) — legacy quotes saved pre-Iter-333 have
          // no `basis` field on their line items. Resolve it here from
          // the item's unit/model strings so downstream rendering + the
          // pricing engine both see the correct cadence. The engine
          // ALSO does this defensively via inferBasis(), but resolving
          // once at load time keeps the state consistent for edits.
          const rawItems = q.line_items || [];
          const normalizedItems = ensureLineIds(rawItems.map(item => {
            if (item && item.basis) return item;
            return { ...item, basis: engineInferBasis(item) };
          }));
          setLineItems(normalizedItems);
          setNotes(q.notes || "");
          setInternalNotes(q.internal_notes || "");
          setTerms(q.terms || "");
          prevTermCatsRef.current = new Set(lineItemCategories(normalizedItems, activeItems.length ? activeItems : PRODUCTS_CATALOG));
          // Loaded/duplicated quotes keep their saved Terms verbatim — never
          // auto-clobber them (duplicate = copied as-is).
          setUserHasEditedTerms(true);
          setValidUntil(q.valid_until || "");
          setNoExpiry(!q.valid_until);
          setQuoteNumber(q.quote_number || "");
          setQuoteOwner({ name: q.owner_name || "", email: q.owner_email || "" });
          setCollectionsEstimate(q.collections_estimate || 0);
          if (Array.isArray(q.ehi_programs)) setEhiPrograms(q.ehi_programs);
          if (q.ehi_computed) setEhiComputed(q.ehi_computed);
          if (q.status) setQuoteStatus(String(q.status).toLowerCase());
          // Iter 188.46 · Phase 3 — hydrate proposal state (safe:
          // absent fields default to off + empty list so old quotes
          // load unchanged).
          if (Array.isArray(q.proposal_sections)) setProposalSections(q.proposal_sections);
          setProposalIncluded(!!q.proposal_included);

          // Fix (Jun 2026 — Hamza): existing quotes stored only lead_id /
          // contact_id, but the paginated /api/leads + /api/contacts lists
          // rarely contain that specific record, so `selectedLead` /
          // `selectedContact` resolved to undefined — leaving AI Draft
          // Email's recipient empty ("no email on file"). Mirror the
          // new-quote prefill enrichment: fetch the quote's lead + contact
          // by id and inject them into the lists so the recipient
          // pre-fills (email or SMS phone) and the AI draft can use the
          // lead's specialty / pain points. Fall back to the email stored
          // on the quote itself when the record can't be fetched.
          setQuoteContactEmail(q.contact_email || "");
          if (q.lead_id && !(Array.isArray(leadsRes.data) ? leadsRes.data : []).some(l => l.id === q.lead_id)) {
            try {
              const lr = await axios.get(`${API}/leads/${q.lead_id}`, { headers });
              if (lr?.data?.id) setLeads(prev => [lr.data, ...prev]);
            } catch { /* lead 404 — fall back to quote.contact_email */ }
          }
          if (q.contact_id) {
            const clist = contactsRes.data?.contacts || contactsRes.data || [];
            if (!(Array.isArray(clist) && clist.some(c => c.id === q.contact_id))) {
              try {
                const cr = await axios.get(`${API}/contacts/${q.contact_id}`, { headers });
                if (cr?.data?.id) setContacts(prev => [cr.data, ...prev]);
              } catch { /* contact 404 */ }
            }
          }
        }

        // Pre-fill from search params
        const prefillContact = searchParams.get("contact_id");
        const prefillLead = searchParams.get("lead_id");
        if (prefillContact && !isEdit) setContactId(prefillContact);
        if (prefillLead && !isEdit) setLeadId(prefillLead);

        // Iter 177 (Iratzi #8070fe73 Feb 2026) — "When you select a lead
        // and click 'New Quote', it doesn't populate the lead information
        // into the quote module." Root cause: `/api/leads` is paginated
        // and the prefilled lead_id is usually NOT on page 1 when there
        // are thousands of leads → `selectedLead` resolves to undefined
        // → company / title fields all render blank. Fix: explicitly
        // fetch the prefilled lead (and contact) by id and merge into
        // the dropdown lists so `selectedLead.find()` returns a hit.
        if (prefillLead && !isEdit) {
          const haveIt = (Array.isArray(leadsRes.data) ? leadsRes.data : [])
            .some(l => l.id === prefillLead);
          if (!haveIt) {
            try {
              const r = await axios.get(`${API}/leads/${prefillLead}`, { headers });
              if (r?.data?.id) setLeads(prev => [r.data, ...prev]);
            } catch { /* lead 404 — leave selectedLead undefined */ }
          }
        }
        if (prefillContact && !isEdit) {
          const list = contactsRes.data?.contacts || contactsRes.data || [];
          const haveIt = Array.isArray(list) && list.some(c => c.id === prefillContact);
          if (!haveIt) {
            try {
              const r = await axios.get(`${API}/contacts/${prefillContact}`, { headers });
              if (r?.data?.id) setContacts(prev => [r.data, ...prev]);
            } catch { /* contact 404 — leave selectedContact undefined */ }
          }
        }
      } catch (e) { toast.error("Failed to load data"); }
      finally { setLoading(false); }
    };
    load();
  }, [id, token, isEdit, searchParams]);

  // Selected contact/lead objects
  const selectedContact = useMemo(() => contacts.find(c => c.id === contactId), [contacts, contactId]);
  const selectedLead = useMemo(() => leads.find(l => l.id === leadId), [leads, leadId]);
  const isEhiRecipient = recipientType === "ehi_client";
  const recipientPayload = isEhiRecipient
    ? { recipient_type: "ehi_client", ehi_client: ehiClient, contact_id: null, lead_id: null }
    : { recipient_type: "lead", ehi_client: null };
  const switchRecipientType = async (next) => {
    const filled = isEhiRecipient ? !!ehiClient : !!(leadId || contactId);
    if (filled) {
      const ok = await confirmDialog({ title: "Switch recipient type?", body: "The recipient you picked will be cleared." });
      if (!ok) return;
    }
    setLeadId(""); setContactId(""); setEhiClient(null); setRecipientType(next);
  };

  // Auto-generate title
  useEffect(() => {
    if (!isEdit && !title && lineItems.length > 0 && (selectedContact || selectedLead || ehiClient?.clinic)) {
      const productName = lineItems[0]?.product || "";
      const company = selectedContact?.company || selectedLead?.company || ehiClient?.clinic || "";
      if (productName && company) setTitle(`${productName} — ${company}`);
    }
  }, [lineItems, selectedContact, selectedLead, ehiClient, title, isEdit]);

  // Catalog helpers
  const addedProductNames = new Set(lineItems.map(i => i.product));

  const addFromCatalog = (product) => {
    if (addedProductNames.has(product.name)) {
      setLineItems(prev => prev.filter(i => i.product !== product.name));
      return;
    }
    setLineItems(prev => {
      // Iter 140.6 — discount lines need extra fields. Initialise the
      // discount-specific fields here so the row renders correctly on
      // first add (discount_type / discount_amount / target_product /
      // expires_at) and so `effectiveDiscountTotal` evaluates cleanly.
      const isDiscount = product.model === "setup_discount" || product.model === "product_discount";
      const newRow = {
        product: product.name, description: product.description,
        pricing_model: product.model, quantity: 1, unit_price: product.price,
        unit_label: product.unit, total: product.price, original_price: product.price,
        // Iter 333 (Option A) — propagate cadence fields from catalog so
        // the pricing engine can route this line to the correct bucket.
        basis: product.basis,
        kind: product.kind,
      };
      if (isDiscount) {
        newRow.discount_type = "percentage"; // % | fixed
        newRow.discount_amount = 0;
        if (product.model === "product_discount") {
          // Iter 177 (Iratzi #4a277f79 Feb 2026) — "Product Discount is
          // not calculated into the total on the quote." Root cause:
          // `target_product` defaulted to "" so `computeDiscountReduction`
          // couldn't resolve a target line and silently returned 0,
          // meaning the persisted `discount_amount` (in the save payload)
          // was 0 and the customer-facing total never reflected the
          // discount. Auto-pick the FIRST non-discount line as the
          // default target so the dollar reduction is non-zero from the
          // moment the rep adds the discount; the rep can still change
          // it from the dropdown.
          const firstTarget = (prev || []).find(
            l => l.pricing_model !== "setup_discount"
              && l.pricing_model !== "product_discount"
              && l.product
          );
          newRow.target_product = firstTarget?.product || "";
          newRow.expires_at = "";    // ISO yyyy-mm-dd
        }
      }
      const next = appendWithAutoAttach(prev, newRow);
      // Iter 140.3 — auto-add Clearinghouse Fees when a PMS or RCM
      // bundle / RevQ / Revenue Cycle Service is added, so reps don't
      // forget to bill the per-transaction clearinghouse charges.
      // Qty defaults to 1; rep adjusts as needed.
      if (
        CLEARINGHOUSE_TRIGGER_NAMES.has(product.name) &&
        !next.some(i => i.product === "Clearinghouse Fees")
      ) {
        const ch = ACTIVE_CATALOG.find(p => p.name === "Clearinghouse Fees");
        if (ch) {
          return appendWithAutoAttach(next, {
            product: ch.name, description: ch.description,
            pricing_model: ch.model, quantity: 1, unit_price: ch.price,
            unit_label: ch.unit, total: ch.price, original_price: ch.price,
            basis: ch.basis, kind: ch.kind,   // Iter 333 (Option A)
            auto_added_for: product.name,
          });
        }
      }
      return next;
    });
  };

  const addCustomItem = () => {
    setLineItems(prev => appendWithAutoAttach(prev, {
      product: "", description: "", pricing_model: "fixed",
      quantity: 1, unit_price: 0, unit_label: "/mo", total: 0, isCustom: true,
      basis: "fixed",   // Iter 333 (Option A) — custom items default to fixed monthly
    }));
  };

  // Iter 188.44 · Phase 2-A — insert a section-heading row so reps
  // can visually group line items ("Care coordination", "Setup",
  // etc.) without adding a priced line. Renders as a bold underlined
  // divider row with no qty/rate/amount cells and no basis math.
  const addSectionHeading = () => {
    setLineItems(prev => appendWithAutoAttach(prev, {
      product: "Section heading",
      description: "",
      pricing_model: "heading",
      quantity: 0,
      unit_price: 0,
      unit_label: "",
      total: 0,
      isHeading: true,
      kind: "heading",
      basis: "fixed",   // ignored by the pricing engine because total=0
    }));
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unit_price) || 0;
      if (updated[index].pricing_model === "percentage") {
        updated[index].total = (collectionsEstimate * price / 100);
      } else {
        updated[index].total = qty * price;
      }
      // Iter 140.3 — when the rep changes the unit_price on a
      // percentage product, the embedded percentage in the description
      // ("@ 6% monthly collections") was going stale. Re-inject the
      // live price into any `@ <number>%` literal that appears in the
      // original product description so the description always
      // reflects the actual unit price. Skipped once the rep has
      // manually edited the description (descriptionEdited flag).
      //
      // Iter 183 (Iratzi #7e4168c8 Feb 2026) — same problem for
      // dollar-tokened descriptions (Clearinghouse Fees: "@ $0.15
      // each"). When the rep edits the price down to $0.10, the
      // description used to keep showing the old literal because we
      // only swapped percentages. We now also handle `@ $N.NN` tokens
      // across every pricing model.
      if (field === "unit_price" && !updated[index].descriptionEdited) {
        const original = ACTIVE_CATALOG.find(p => p.name === updated[index].product);
        if (original) {
          let nextDesc = updated[index].description;
          if (updated[index].pricing_model === "percentage") {
            nextDesc = syncPercentageDescription(original.description, price, original.price);
          }
          // Dollar-token resync applies to ALL pricing models — only
          // descriptions that contain the `@ $N.NN` literal are
          // affected, so this is a safe no-op for items without one.
          nextDesc = syncDollarDescription(nextDesc, price);
          updated[index].description = nextDesc;
        }
      }
      if (field === "description") {
        updated[index].descriptionEdited = true;
      }
      return updated;
    });
  };

  const removeLineItem = (index) => setLineItems(prev => {
    // Iter 188.44 · Phase 2-A — removing a base subscription cascades
    // to its attached seats. Non-base rows fall through to the
    // straight `.filter(_, i)` path with an empty `cascadedIds`.
    const { next, cascadedIds } = removeWithCascade(prev, index);
    if (cascadedIds.length > 0) {
      // Fire a soft toast so the rep knows the seats went too — no
      // confirmation modal (would break the current one-click flow),
      // but the toast makes the side-effect visible and undo-able via
      // history/back if the rep clicked in error.
      try {
        toast(`Removed base subscription — ${cascadedIds.length} attached seat${cascadedIds.length === 1 ? "" : "s"} also removed.`);
      } catch (_) { /* toast unavailable in tests */ }
    }
    return next;
  });

  // Iter 188.54 — legacy native HTML5 drag state (dragSrcIdx /
  // dragOverIdx) replaced by @dnd-kit's `useSortable`. Removed.
  // Iter 331 — surface the quote's current status in the appbar pill.
  // Defaults to "draft" for new quotes; hydrated from the API on edit.
  const [quoteStatus, setQuoteStatus] = useState("draft");
  // Iter 331.2 — Gap 8: Builder / PDF preview seg switcher (stub).
  const [pdfPreviewMode, setPdfPreviewMode] = useState(false);
  // Iter 331.2 — Gap 7: "Browse all products" 3-column modal.
  const [browseProductsOpen, setBrowseProductsOpen] = useState(false);
  // Iter 331.3 — Gap 2: Metadata strip collapse state. Auto-collapses
  // once both a Contact and a Lead are chosen so the summary is a
  // one-line strip instead of a full picker card.
  const [metadataCollapsed, setMetadataCollapsed] = useState(false);
  // Iter 188.54 — @dnd-kit sensors. Pointer sensor with an 8px
  // activation distance so genuine clicks (input focus, button press)
  // never accidentally start a drag. Keyboard sensor exposes Space to
  // pick up + arrow keys to move for accessibility.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleLineItemDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIdx = lineItems.findIndex((li) => li._lineId === active.id);
    const toIdx = lineItems.findIndex((li) => li._lineId === over.id);
    if (fromIdx < 0 || toIdx < 0) return;
    reorderLineItem(fromIdx, toIdx);
  };

  const reorderLineItem = (from, to) => {
    setLineItems(prev => reorderLineItemHelper(prev, from, to));
  };

  // Section 11B — AI product suggestions
  const suggestProductsWithAI = async () => {
    setAiSuggesting(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const res = await axios.post(`${API}/ai/quote-suggest-products`, {
        specialty: selectedLead?.specialty || selectedContact?.specialty || "",
        products_of_interest: selectedLead?.products_of_interest || selectedLead?.products || [],
        stage: selectedLead?.stage || "",
        notes: selectedLead?.notes || selectedLead?.summary || "",
        catalog: ACTIVE_CATALOG.map(p => ({ name: p.name, category: p.category, price: p.price, unit: p.unit })),
      }, { headers });
      setAiSuggestions(res.data || null);
      if (!res.data?.suggestions?.length) toast.message("AI couldn't find a strong fit — pick manually.");
    } catch (e) {
      toast.error("AI suggestion failed");
    } finally {
      setAiSuggesting(false);
    }
  };

  const acceptAISuggestion = (s) => {
    const product = ACTIVE_CATALOG.find(p => p.name === s.name);
    if (!product) return;
    if (!addedProductNames.has(product.name)) addFromCatalog(product);
  };

  // ── ✦ AI-drafted quote email (review-before-send) ──
  const openAIEmailModal = async () => {
    const contact = selectedContact;
    const lead = selectedLead;
    const recipient = (isEhiRecipient ? ehiClient?.email : "") || contact?.email || lead?.email || lead?.contact_email || quoteContactEmail || "";
    if (!recipient) {
      toast.error("This lead/contact has no email on file");
      return;
    }
    if (lineItems.length === 0) {
      toast.error("Add at least one product before drafting an email");
      return;
    }
    if (!title.trim()) {
      toast.error("Quote title is required first");
      return;
    }

    // Open modal in "drafting" state so the user sees a spinner immediately
    setEmailModal({
      subject: "", body: "", to: recipient,
      drafting: true, sending: false, savedQuote: null,
    });

    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Save the quote first (or update it) so we have a quote_number / id.
      const data = {
        title, contact_id: contactId || null, lead_id: leadId || null, ...recipientPayload,
        line_items: lineItems, subtotal, discount_percent: 0, discount_amount: 0,
        tax_percent: 0, tax_amount: 0, total: subtotal, notes, internal_notes: internalNotes, terms,
        valid_until: noExpiry ? null : (validUntil || null),
        status: "draft", collections_estimate: collectionsEstimate,
        // Iter 188.46 · Phase 3 — persist proposal state alongside
        // the quote. Backend accepts arbitrary extras on line_items
        // and top-level (see server.py QuoteCreate).
        proposal_included: !!proposalIncluded,
        proposal_sections: proposalSections,
      };
      let savedQuote;
      if (isEdit) {
        const res = await axios.put(`${API}/quotes/${id}`, data, { headers });
        savedQuote = res.data;
      } else {
        const res = await axios.post(`${API}/quotes`, data, { headers });
        savedQuote = res.data;
      }

      // Ask Claude for a cover email
      const draftRes = await axios.post(`${API}/ai/quote-email-draft`, {
        quote_title: title,
        quote_number: savedQuote?.quote_number || "",
        products: lineItems.map(i => i.product).join(", "),
        total: subtotal,
        valid_until: noExpiry ? "Open-ended" : (validUntil || "30 days"),
        contact_name: contact ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim() : (lead?.contact_name || ""),
        company: contact?.company || lead?.company || "",
        specialty: lead?.specialty || "",
        pain_points: Array.isArray(lead?.pain_points) ? lead.pain_points.join(", ") : (lead?.pain_points || ""),
      }, { headers });

      setEmailModal({
        subject: draftRes.data?.subject || `Your Enable Healthcare Quote — ${title}`,
        body: draftRes.data?.body || "",
        to: recipient,
        drafting: false, sending: false,
        savedQuote,
      });
    } catch (e) {
      toast.error("Couldn't draft email — try again");
      setEmailModal(null);
    }
  };

  const sendAIEmail = async () => {
    if (!emailModal) return;
    if (!emailModal.subject.trim() || !emailModal.body.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setEmailModal(prev => ({ ...prev, sending: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/communications/send`, {
        lead_id: leadId || null,
        type: "email",
        direction: "outbound",
        channel: "email",
        subject: emailModal.subject,
        content: emailModal.body,
        to: emailModal.to,
      }, { headers });
      toast.success(`Quote email sent to ${emailModal.to}`);
      const sentQuote = emailModal.savedQuote;
      setEmailModal(null);
      if (sentQuote?.id) setPostSaveQuote({ ...sentQuote, status: sentQuote.status || "draft" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = (typeof detail === "object" && detail?.code) || e?.response?.status || "";
      const friendly = {
        no_sender_available: "Email service isn't connected. Ask your admin to connect Microsoft 365 in Settings.",
        m365_not_configured: "Microsoft 365 isn't configured yet.",
        m365_oauth_required: "Your Microsoft 365 mailbox isn't connected. Go to Settings → Integrations → Microsoft 365 to reconnect, then send again.",
      }[code];
      const msg = friendly
        || (typeof detail === "string" ? detail : detail?.message)
        || (code ? `Send failed (${code})` : "Send failed");
      toast.error(msg);
      setEmailModal(prev => ({ ...prev, sending: false }));
    }
  };

  const regenerateAIEmail = async () => {
    if (!emailModal) return;
    setEmailModal(prev => ({ ...prev, drafting: true }));
    try {
      const contact = selectedContact;
      const lead = selectedLead;
      const draftRes = await axios.post(`${API}/ai/quote-email-draft`, {
        quote_title: title,
        quote_number: emailModal.savedQuote?.quote_number || "",
        products: lineItems.map(i => i.product).join(", "),
        total: subtotal,
        valid_until: noExpiry ? "Open-ended" : (validUntil || "30 days"),
        contact_name: contact ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim() : (lead?.contact_name || ""),
        company: contact?.company || lead?.company || "",
        specialty: lead?.specialty || "",
        pain_points: Array.isArray(lead?.pain_points) ? lead.pain_points.join(", ") : (lead?.pain_points || ""),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEmailModal(prev => ({
        ...prev,
        subject: draftRes.data?.subject || prev.subject,
        body: draftRes.data?.body || prev.body,
        drafting: false,
      }));
      toast.success("Re-drafted with AI");
    } catch (e) {
      toast.error("Re-draft failed");
      setEmailModal(prev => ({ ...prev, drafting: false }));
    }
  };

  // Totals
  const subtotal = lineItems.reduce((sum, item) => {
    // Iter 140.6 — discount lines are negative adjustments computed
    // separately; exclude their stored total here to avoid double-
    // counting.
    if (item.pricing_model === "setup_discount" || item.pricing_model === "product_discount") return sum;
    return sum + (parseFloat(item.total) || 0);
  }, 0);
  const discountReduction = totalDiscountReduction(lineItems);
  const hasPercentageItems = lineItems.some(i => i.pricing_model === "percentage");
  // Iter 140.3 — flat monthly recurring excludes percentage items
  // (because those scale with collections, not a fixed amount) and
  // one-time fees (unit_label === "one-time" or *_one-time). Used to
  // power the second line in the per-line collections breakdown box.
  const flatMonthlyRecurring = flatMonthlyRecurringTotal(lineItems);

  // Iter 333 (Option A) — cadence-aware pricing buckets. Each item is
  // routed by its `basis` (or inferred from `unit`/`model` for legacy
  // pre-Iter-333 quotes) into one of 4 buckets: fixedMonthly,
  // volumeMonthly, annual, oneTime. Percentage lines are surfaced as
  // rate lines (never summed into a dollar figure) per spec §4.
  //
  // `pricingBuckets` is memoized on lineItems shape. It's the source of
  // truth for the right-panel summary and the sticky bar. The legacy
  // `subtotal` computed above is still used in the persistence payload
  // (`quote.subtotal` / `quote.total`) but the DISPLAY is now correct.
  const pricingBuckets = useMemo(() => engineTotals(lineItems), [lineItems]);
  const trueMonthlyMRR = (pricingBuckets.fixedMonthly || 0) + (pricingBuckets.volumeMonthly || 0);
  const showAnnualHeadline = !engineHasRateLines(lineItems);
  const engineSentence = engineSummarySentence(lineItems);

  // Update percentage items when collections estimate changes
  useEffect(() => {
    if (hasPercentageItems) {
      setLineItems(prev => prev.map(item => {
        if (item.pricing_model === "percentage") {
          return { ...item, total: collectionsEstimate * (parseFloat(item.unit_price) || 0) / 100 };
        }
        return item;
      }));
    }
  }, [collectionsEstimate, hasPercentageItems]);

  // Filtered catalog
  // Iter 331.3 — Gap 3: catalog search now matches product name,
  // description, category, AND CPT codes (e.g. typing "99457" or
  // "G0557" finds the corresponding care-coordination program).
  const filteredCatalog = ACTIVE_CATALOG.filter(p => {
    if (!catalogSearch) return true;
    const q = catalogSearch.toLowerCase();
    const cpt = String(p.cpt || p.cpt_code || "").toLowerCase();
    const hay = [p.name, p.description, p.category, cpt, p.model].join(" ").toLowerCase();
    return hay.includes(q);
  });

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      // Iter 334 (Option B.1) — persist across sessions
      try { localStorage.setItem(CATALOG_EXPANDED_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  };

  // Iter 334 (Option B.1) — when the rep types a search query, auto-
  // expand any group containing a matching product so results are
  // visible without a second click. Restores prior state when the
  // query is cleared.
  const [preSearchExpanded, setPreSearchExpanded] = useState(null);
  useEffect(() => {
    if (catalogSearch.trim()) {
      if (preSearchExpanded === null) setPreSearchExpanded(expandedCategories);
      const q = catalogSearch.toLowerCase();
      const matchingCats = new Set();
      (PRODUCTS_CATALOG.concat(ACTIVE_CATALOG || [])).forEach(p => {
        if ((p.name + " " + (p.description || "")).toLowerCase().includes(q)) {
          matchingCats.add(p.category);
        }
      });
      setExpandedCategories(matchingCats);
    } else if (preSearchExpanded !== null) {
      setExpandedCategories(preSearchExpanded);
      setPreSearchExpanded(null);
    }
  }, [catalogSearch]);

  // Save handler
  const handleSave = async (submitForApproval = false) => {
    if (!title.trim()) { toast.error("Quote title is required"); return; }
    if (lineItems.length === 0 && ehiPrograms.length === 0) { toast.error("Add at least one line item or EHI program"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const ehiMonthly = ehiComputed?.totals?.monthly_recurring || 0;
      const ehiOneTime = ehiComputed?.totals?.one_time || 0;
      // Iter 176.8 (Iratzi — issue 894a67e6) — `discountReduction` was
      // displayed in the edit view but never persisted onto the quote
      // total, so the customer-facing quote still showed the pre-
      // discount amount. Now we subtract the setup-fee discount from
      // the grand total AND store discount_amount so QuoteDetail can
      // render the "Discount" line.
      const grandTotal = Math.max(0, subtotal + ehiMonthly - discountReduction);
      const data = {
        title, contact_id: contactId || null, lead_id: leadId || null, ...recipientPayload,
        line_items: lineItems, subtotal,
        discount_percent: 0,
        discount_amount: discountReduction,
        tax_percent: 0, tax_amount: 0, total: grandTotal, notes, internal_notes: internalNotes, terms,
        valid_until: noExpiry ? null : (validUntil || null),
        status: "draft", collections_estimate: collectionsEstimate,
        ehi_programs: ehiPrograms.length > 0 ? ehiPrograms : null,
        ehi_computed: ehiComputed || null,
        // Iter 333 (Option A) — persist the cadence-aware bucket totals
        // additively so downstream consumers (PDF export, reports,
        // approvals view) don't have to re-run the engine. The legacy
        // `subtotal` + `total` fields above stay as-is for BC.
        pricing_buckets: pricingBuckets,
        true_monthly_mrr: trueMonthlyMRR,
        // Iter 188.46 · Phase 3 — persist Proposal state on the main
        // save path too (this is the one hit by the "Save quote"
        // button). Backend accepts arbitrary extras on QuoteCreate.
        proposal_included: !!proposalIncluded,
        proposal_sections: proposalSections,
      };
      let savedQuote;
      if (isEdit) {
        const res = await axios.put(`${API}/quotes/${id}`, data, { headers });
        savedQuote = res.data;
      } else {
        const res = await axios.post(`${API}/quotes`, data, { headers });
        savedQuote = res.data;
      }
      if (submitForApproval && savedQuote?.id) {
        // Submit for approval: Draft -> Pending Approval
        await axios.post(`${API}/quotes/${savedQuote.id}/submit-for-approval`, {}, { headers });
        // Iter 140.6 (user-requested) — instead of showing the
        // misleading "ready to send" PostSaveScreen, route directly to
        // the QuoteDetail page which now renders a "Submitted for
        // approval · you'll be notified once the contract desk
        // approves" banner. The PostSaveScreen no longer renders for
        // submit-for-approval flows.
        toast.success(`${savedQuote.quote_number} submitted — you'll be notified once approved`);
        navigate(`/quotes/${savedQuote.id}`);
        return;
      } else {
        toast.success(`${savedQuote.quote_number || "Quote"} saved as draft`);
      }
      // 2F — When invoked via Lead Focus "Create quote" sub-status, flip
      // the lead back to "Proposal sent" + return to the lead page.
      const onSaveSetSubStatus = location.state?.onSaveSetSubStatus;
      const returnHome = location.state?.returnTo;
      if (onSaveSetSubStatus && leadId) {
        try {
          await axios.put(`${API}/leads/${leadId}`, { sub_status: onSaveSetSubStatus }, { headers });
        } catch { /* non-fatal */ }
      }
      if (onSaveSetSubStatus && returnHome) {
        navigate(returnHome);
      }
    } catch { toast.error("Failed to save quote"); }
    finally { setSaving(false); }
  };

  // AI summary
  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await axios.post(`${API}/ai/quote-summary`, {
        company: selectedContact?.company || selectedLead?.company || "",
        contact_name: selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : selectedLead?.contact_name || "",
        specialty: selectedLead?.specialty || "",
        products: lineItems.map(i => i.product).join(", "),
        total: subtotal,
        pain_points: selectedLead?.pain_points?.join(", ") || "",
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.summary) {
        setNotes(prev => prev ? `${prev}\n\n${res.data.summary}` : res.data.summary);
        toast.success("Summary added to notes");
      }
    } catch { toast.error("AI unavailable"); }
    finally { setGeneratingSummary(false); }
  };

  // Navigate to comms with quote context
  const sendViaComms = (mode = "email") => {
    const q = postSaveQuote || {};
    const contact = selectedContact;
    sessionStorage.setItem("quote_context", JSON.stringify({
      quote: q, contact: contact ? { name: `${contact.first_name} ${contact.last_name}`, email: contact.email, phone: contact.phone, company: contact.company } : null,
      lead: selectedLead, mode,
    }));
    navigate("/communications");
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 52px)" }}>
        <Loader2 style={{ width: 24, height: 24, animation: "spin 0.8s linear infinite", color: "var(--blue)" }} />
      </div>
    );
  }

  // Post-save screen
  if (postSaveQuote) {
    return (
      <PostSaveScreen
        quote={postSaveQuote}
        contact={selectedContact ? { email: selectedContact.email, phone: selectedContact.phone } : {}}
        onGoBack={() => navigate(returnTo)}
        onSendEmail={() => sendViaComms("email")}
        onSendSMS={() => sendViaComms("sms")}
      />
    );
  }

  const contactObj = selectedContact ? { name: `${selectedContact.first_name} ${selectedContact.last_name}`, company: selectedContact.company } : selectedLead ? { name: selectedLead.contact_name, company: selectedLead.company } : null;
  const previewContact = isEhiRecipient && ehiClient ? { name: ehiClient.clinic, company: `EHI Client ID ${ehiClient.client_id}` } : contactObj;

  return (
    <div data-testid="quote-builder-page" data-page="quote-builder" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 52px)", background: "var(--qb-bg, #f4f6fa)", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
      {/* Iter 331 (Jul 2026) — Quote Builder re-skin per the design mock.
          All tokens are SCOPED to [data-page="quote-builder"] so the
          global sidebar / topnav / other pages are unaffected. Existing
          handlers, state, effects, and testids are 100% preserved —
          this is purely a visual layer on top of the existing shell. */}
      <style>{`
        [data-page="quote-builder"] {
          --qb-blue:  #2563eb;
          --qb-navy:  #1b3a5c;
          --qb-purple:#6d40c8;
          --qb-amber: #b45309;
          --qb-ink:   #0f172a;
          --qb-ink-2: #3a475a;
          --qb-muted: #6b7a90;
          --qb-faint: #9aa8bb;
          --qb-line:  #e5eaf1;
          --qb-hair:  #eff3f8;
          --qb-bg:    #f4f6fa;
          --qb-rail:  #e8edf5;
          --qb-paper-shadow: 0 1px 2px rgba(16,24,40,.05), 0 14px 30px -16px rgba(16,24,40,.28);
        }
        [data-page="quote-builder"] .qb-paper {
          background: #fff;
          border: 1px solid var(--qb-line);
          border-radius: 12px;
          box-shadow: var(--qb-paper-shadow);
        }
        [data-page="quote-builder"] .qb-appbar {
          background: #fff;
          border-bottom: 1px solid var(--qb-line);
        }
        [data-page="quote-builder"] .qb-title-input {
          font-size: 17px;
          font-weight: 700;
          color: var(--qb-ink);
          letter-spacing: -0.01em;
        }
        [data-page="quote-builder"] .qb-status {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 9px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        [data-page="quote-builder"] .qb-status.draft    { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }
        [data-page="quote-builder"] .qb-status.pending  { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
        [data-page="quote-builder"] .qb-status.approved { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
        [data-page="quote-builder"] .qb-status.sent     { background: #dbeafe; color: #1e40af; border: 1px solid #93c5fd; }
        [data-page="quote-builder"] .qb-status.rejected { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
        [data-page="quote-builder"] .qb-rail {
          background: var(--qb-rail);
          border-right: 1px solid var(--qb-line);
        }
        [data-page="quote-builder"] .qb-primary {
          background: var(--qb-navy) !important;
          color: #fff !important;
          border: 1px solid var(--qb-navy) !important;
        }
        [data-page="quote-builder"] .qb-primary:hover:not(:disabled) {
          background: #12283f !important;
          border-color: #12283f !important;
        }
        [data-page="quote-builder"] .qb-mut { color: var(--qb-muted); }
        [data-page="quote-builder"] .qb-summary-sentence {
          font-size: 12px;
          color: var(--qb-ink-2);
          line-height: 1.55;
        }
        [data-page="quote-builder"] .qb-summary-sentence strong {
          color: var(--qb-ink);
          font-weight: 700;
        }
        [data-page="quote-builder"] .qb-summary-sentence .amt {
          color: var(--qb-navy);
          font-weight: 750;
          font-variant-numeric: tabular-nums;
        }
        /* Iter 331.2 — Gap 9: numeric cells right-align with tabular figures. */
        [data-page="quote-builder"] .num, [data-page="quote-builder"] input[type="number"] {
          font-variant-numeric: tabular-nums;
        }
      `}</style>
      {/* Top bar — Iter 331 re-skinned as "appbar" */}
      <div className="qb-appbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button onClick={() => navigate(returnTo)} className="btn" style={{ padding: "5px 10px", fontSize: 11, background: "transparent", border: "1px solid var(--qb-line)", color: "var(--qb-ink-2)" }} data-testid="back-to-quotes">
            <ArrowLeft style={{ width: 14, height: 14 }} /> Back
          </button>
          {/* Iter 334 — "Browse quotes" button removed: the sidebar now
             lands on the list view directly, so this navigation is
             redundant. The Back button above handles list return. */}
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Quote title..."
            className="qb-title-input"
            style={{ border: "none", outline: "none", background: "transparent", width: 420, fontFamily: "inherit" }}
            data-testid="quote-title-input" />
          {(() => {
            // Iter 331 — status pill (derived from `quoteStatus` state; loaded from API on edit, "draft" for new).
            const s = String(quoteStatus || "draft").toLowerCase();
            const cls = ["draft", "pending", "approved", "sent", "rejected"].includes(s) ? s : (s.includes("approv") ? "pending" : "draft");
            const label = cls.charAt(0).toUpperCase() + cls.slice(1);
            return <span className={`qb-status ${cls}`} data-testid="quote-status-pill">{label}</span>;
          })()}
          {quoteNumber && <span className="qb-mut" style={{ fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{quoteNumber}</span>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Iter 331.2 — Builder / PDF preview seg switcher (Gap 8, stub). */}
          <div style={{ display: "inline-flex", background: "#f1f5f9", border: "1px solid var(--qb-line)", borderRadius: 8, padding: 2, marginRight: 4 }} data-testid="qb-view-switcher">
            <button onClick={() => setPdfPreviewMode(false)} className="btn"
              data-testid="qb-view-builder"
              style={{ padding: "4px 12px", fontSize: 11, background: pdfPreviewMode ? "transparent" : "#fff", border: "none", borderRadius: 6, color: pdfPreviewMode ? "var(--qb-muted)" : "var(--qb-ink)", fontWeight: pdfPreviewMode ? 500 : 600, boxShadow: pdfPreviewMode ? "none" : "0 1px 2px rgba(0,0,0,.05)" }}>
              Builder
            </button>
            <button onClick={() => setPdfPreviewMode(true)} className="btn"
              data-testid="qb-view-pdf"
              style={{ padding: "4px 12px", fontSize: 11, background: pdfPreviewMode ? "#fff" : "transparent", border: "none", borderRadius: 6, color: pdfPreviewMode ? "var(--qb-ink)" : "var(--qb-muted)", fontWeight: pdfPreviewMode ? 600 : 500, boxShadow: pdfPreviewMode ? "0 1px 2px rgba(0,0,0,.05)" : "none" }}>
              PDF preview
            </button>
          </div>
          <button onClick={() => handleSave(false)} disabled={saving} className="btn" style={{ fontSize: 11, padding: "7px 14px", background: "#fff", border: "1px solid var(--qb-line)", color: "var(--qb-ink-2)" }} data-testid="save-draft-btn">
            {saving ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Save style={{ width: 12, height: 12 }} />} Save Draft
          </button>
          <button
            onClick={openAIEmailModal}
            disabled={saving || lineItems.length === 0 || (!contactId && !leadId)}
            className="btn"
            data-testid="ai-email-quote-btn"
            style={{ fontSize: 11, padding: "7px 14px", background: "#EEEDFE", border: "1px solid #CECBF6", color: "#3C3489", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Sparkles style={{ width: 12, height: 12 }} /> AI Draft Email
          </button>
          <button onClick={() => handleSave(true)} disabled={saving} className="btn qb-primary" style={{ fontSize: 11, padding: "7px 16px" }} data-testid="send-quote-btn">
            {saving ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Send style={{ width: 12, height: 12 }} />} Submit for Approval
          </button>
        </div>
      </div>

      {/* Three-panel layout */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* LEFT PANEL — Product Selector (Iter 331 restyled as "rail") */}
        <div className="qb-rail" style={{ width: 260, flexShrink: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "14px 14px 10px" }}>
            {/* Iter 334 (Option B.1) — header shows total SKU count and
               a collapse-state hint, matching the mockup's
               "CATALOG · 42 SKUs, collapsed" affordance. */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--qb-ink)", letterSpacing: "-0.005em" }}>Catalog</div>
              <div style={{ fontSize: 9, color: "var(--qb-muted)", fontWeight: 600, padding: "1px 6px", borderRadius: 10, background: "#eef2f7", border: "0.5px solid var(--qb-line)" }} data-testid="qb-catalog-count-pill">
                {ACTIVE_CATALOG.length} SKUs{expandedCategories.size === 0 ? ", collapsed" : ""}
              </div>
            </div>
            <div style={{ fontSize: 10, color: "var(--qb-muted)", marginBottom: 10 }}>Add products, services, and programs</div>
            <div style={{ position: "relative" }}>
              <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "var(--text-tertiary)" }} />
              <input value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)} placeholder="Search catalog..."
                className="input" style={{ paddingLeft: 28, fontSize: 11, height: 30 }} data-testid="catalog-search" />
            </div>
            {/* Section 11B — AI product suggestion link */}
            <button
              onClick={suggestProductsWithAI}
              disabled={aiSuggesting || (!leadId && !contactId)}
              data-testid="quote-ai-suggest-btn"
              style={{
                marginTop: 8, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                background: "#EEEDFE", border: "0.5px solid #CECBF6", color: "#3C3489",
                borderRadius: 6, padding: "6px 8px", fontSize: 11, fontWeight: 500, cursor: (aiSuggesting || (!leadId && !contactId)) ? "not-allowed" : "pointer",
                opacity: (!leadId && !contactId) ? 0.55 : 1,
              }}>
              {aiSuggesting ? <Loader2 style={{ width: 11, height: 11, animation: "spin 0.8s linear infinite" }} /> : <Sparkles style={{ width: 11, height: 11 }} />}
              {aiSuggesting ? "Thinking…" : "AI suggests products for this lead"}
            </button>
            {aiSuggestions && (
              <div data-testid="quote-ai-suggestions" style={{ marginTop: 8, background: "#fff", border: "0.5px solid #CECBF6", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#3C3489", textTransform: "uppercase", letterSpacing: 0.3 }}>AI suggestions</div>
                  <button onClick={() => setAiSuggestions(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }} data-testid="quote-ai-suggestions-close"><X style={{ width: 11, height: 11 }} /></button>
                </div>
                {aiSuggestions.rationale && (
                  <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginBottom: 6, fontStyle: "italic" }}>{aiSuggestions.rationale}</div>
                )}
                {(aiSuggestions.suggestions || []).map((s, i) => {
                  const already = addedProductNames.has(s.name);
                  return (
                    <div key={i} data-testid={`quote-ai-suggestion-${i}`} style={{ padding: "6px 8px", borderRadius: 6, background: already ? "var(--green-light)" : "#FAFAFE", marginBottom: 4 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)" }}>{s.name}</span>
                        <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: s.tier === "Core" ? "#3C3489" : s.tier === "Recommended" ? "#2563eb" : "#888", color: "#fff" }}>{s.tier}</span>
                      </div>
                      <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 2 }}>{s.reason}</div>
                      {!already ? (
                        <button onClick={() => acceptAISuggestion(s)} className="btn" data-testid={`quote-ai-accept-${i}`} style={{ fontSize: 10, padding: "3px 8px", marginTop: 4, color: "#3C3489", border: "0.5px solid #CECBF6" }}>+ Add to quote</button>
                      ) : (
                        <span style={{ fontSize: 9, fontWeight: 600, color: "var(--green)", display: "inline-flex", alignItems: "center", gap: 2, marginTop: 4 }}><Check style={{ width: 9, height: 9 }} /> Added</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "0 8px 8px" }}>
            {/* Iter 334 (Option B.1) — "Frequently quoted" pinned section.
               Shown ONLY when NOT searching (so search results feel
               focused on the query). Uses the user's real 30-day history
               if available, else falls back to the mockup's 5 defaults. */}
            {!catalogSearch.trim() && (() => {
              const fromHistory = frequentlyQuoted.items || [];
              const historyNames = new Set(fromHistory.map(x => (x.name || "").trim()));
              // Map history entries → live catalog entries so we render
              // the full product card (price, model pill, etc.). Fallback
              // to canonical defaults if the user has no history.
              const catalogByName = new Map(ACTIVE_CATALOG.map(p => [p.name, p]));
              let picks = fromHistory
                .map(f => catalogByName.get(f.name))
                .filter(Boolean);
              if (picks.length === 0) {
                picks = FREQUENT_FALLBACK
                  .map(n => catalogByName.get(n))
                  .filter(Boolean);
                // De-dupe (Essential seed vs live name might both hit)
                const seen = new Set();
                picks = picks.filter(p => {
                  if (seen.has(p.name)) return false;
                  seen.add(p.name);
                  return true;
                }).slice(0, 5);
              }
              if (picks.length === 0) return null;
              return (
                <div style={{ marginBottom: 8 }} data-testid="qb-frequently-quoted">
                  <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 4px 4px", fontSize: 10, fontWeight: 700, color: "var(--qb-ink-2)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    <Star style={{ width: 10, height: 10, color: "#f59e0b", fill: "#f59e0b" }} />
                    Frequently quoted
                    <span style={{ marginLeft: "auto", color: "var(--qb-muted)", fontWeight: 500, textTransform: "none", letterSpacing: 0, fontSize: 9 }}>
                      {fromHistory.length > 0 ? "your last 30 days" : "suggested"}
                    </span>
                  </div>
                  {picks.map((p, i) => {
                    const added = addedProductNames.has(p.name);
                    const pill = MODEL_PILLS[p.model] || MODEL_PILLS.fixed;
                    // Iter 188.53 — Same hover-expand contract as
                    // regular catalog rows: raise the card, reveal
                    // description and the `+` pill on hover.
                    const freqKey = `freq:${p.name}`;
                    const isHovered = hoveredProduct === freqKey && !added;
                    return (
                      <div key={`freq-${i}`}
                        onMouseEnter={() => setHoveredProduct(freqKey)}
                        onMouseLeave={() => setHoveredProduct(null)}
                        title={added ? "Already in this quote — click to remove" : undefined}
                        style={{
                          padding: "6px 10px", borderRadius: 8, marginBottom: 2,
                          cursor: added ? "not-allowed" : "pointer",
                          background: added ? "#f4f6fa" : (isHovered ? "#ffffff" : "#fffaeb"),
                          border: `0.5px solid ${added ? "var(--qb-line)" : (isHovered ? "#f5c451" : "#fde68a")}`,
                          boxShadow: (!added && isHovered) ? "0 1px 3px rgba(245,158,11,0.18)" : "none",
                          opacity: added ? 0.55 : 1,
                          transition: "background 0.16s, border-color 0.16s, box-shadow 0.16s",
                          position: "relative",
                        }}
                        onClick={() => { if (!added) addFromCatalog(p); }}
                        data-testid={`qb-frequent-${p.name.replace(/\s/g, '-').toLowerCase()}`}>
                        {/* Top row: name + price + hover-only `+` */}
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: added ? "var(--qb-muted)" : "var(--text-primary)", lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {p.name}
                          </div>
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: added ? "var(--qb-muted)" : "var(--blue)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                            ${p.price}{p.unit}
                          </span>
                          {!added && (
                            <span
                              data-testid={`qb-frequent-${p.name.replace(/\s/g, '-').toLowerCase()}-add`}
                              style={{
                                fontSize: 15, fontWeight: 700, color: "#a16207",
                                width: 14, lineHeight: 1, textAlign: "center",
                                opacity: isHovered ? 1 : 0,
                                transition: "opacity 0.15s",
                              }}
                            >+</span>
                          )}
                        </div>
                        {/* Model pill + "already in quote" fallback */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                          <span style={{ fontSize: 8, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: pill.bg, color: pill.color, opacity: added ? 0.5 : 1 }}>{pill.label}</span>
                          {added && (
                            <span style={{ fontSize: 9, fontWeight: 600, color: "var(--qb-muted)" }}>Already in quote</span>
                          )}
                        </div>
                        {/* Description — hidden by default, expands on
                            hover per mockup `.pd` rule. */}
                        {p.description && (
                          <div style={{
                            fontSize: 10.5, color: "var(--qb-muted)",
                            lineHeight: 1.4,
                            maxHeight: isHovered ? 52 : 0,
                            marginTop: isHovered ? 4 : 0,
                            overflow: "hidden",
                            transition: "max-height 0.16s ease, margin-top 0.16s ease",
                          }}>
                            {p.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {CATEGORIES.map(cat => {
              const items = filteredCatalog.filter(p => p.category === cat);
              if (items.length === 0) return null;
              const expanded = expandedCategories.has(cat);
              return (
                <div key={cat} style={{ marginBottom: 4 }}>
                  <button onClick={() => toggleCategory(cat)} style={{
                    display: "flex", alignItems: "center", gap: 4, width: "100%", background: "none", border: "none", cursor: "pointer",
                    padding: "6px 4px", fontSize: 10, fontWeight: 700, color: "var(--qb-ink-2)", textTransform: "uppercase", letterSpacing: 0.4, fontFamily: "inherit"
                  }} data-testid={`qb-cat-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                    {expanded ? <ChevronDown style={{ width: 10, height: 10 }} /> : <ChevronRight style={{ width: 10, height: 10 }} />}
                    {cat}
                    <span style={{ marginLeft: 4, color: "var(--qb-muted)", fontWeight: 600 }} data-testid={`qb-cat-count-${cat.replace(/\s+/g, '-').toLowerCase()}`}>({items.length})</span>
                  </button>
                  {expanded && items.map((p, i) => {
                    const added = addedProductNames.has(p.name);
                    const pill = MODEL_PILLS[p.model] || MODEL_PILLS.fixed;
                    // Iter 187 (Feb 2026) — Match mockup `.p-item` hover
                    // reveal: description + Add pill hidden by default,
                    // expand/opacity-fade in on hover with a smooth
                    // 160ms transition. Kills the external tooltip pop-
                    // out — details now live inline inside the card.
                    const isHovered = hoveredProduct === p.name && !added;
                    return (
                      <div key={i}
                        onMouseEnter={() => setHoveredProduct(p.name)}
                        onMouseLeave={() => setHoveredProduct(null)}
                        title={added ? "Already in this quote — remove it from Line Items to re-add" : undefined}
                        style={{
                          padding: "6px 10px", borderRadius: 8, marginBottom: 2,
                          cursor: added ? "not-allowed" : "pointer",
                          background: added ? "#f4f6fa" : (isHovered ? "#ffffff" : "var(--surface)"),
                          border: `0.5px solid ${added ? "var(--qb-line)" : (isHovered ? "#dfe6f0" : "var(--border)")}`,
                          boxShadow: (!added && isHovered) ? "0 1px 3px rgba(16,24,40,0.10)" : "none",
                          opacity: added ? 0.55 : 1,
                          transition: "background 0.16s, border-color 0.16s, box-shadow 0.16s",
                          position: "relative",
                        }}
                        onClick={() => { if (!added) addFromCatalog(p); }} data-testid={`catalog-${p.name.replace(/\s/g, '-').toLowerCase()}`}>
                        {/* Top row: name + price + optional Add pill on hover */}
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: added ? "var(--qb-muted)" : "var(--text-primary)", lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: added ? "var(--qb-muted)" : "var(--blue)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>${p.price}{p.unit}</span>
                          {!added && (
                            <span
                              data-testid={`catalog-${p.name.replace(/\s/g, '-').toLowerCase()}-add`}
                              style={{
                                fontSize: 15, fontWeight: 700, color: "var(--blue)",
                                width: 14, lineHeight: 1, textAlign: "center",
                                opacity: isHovered ? 1 : 0,
                                transition: "opacity 0.15s",
                              }}
                            >+</span>
                          )}
                        </div>
                        {/* Model pill sits on its own row so the name/
                            price row stays clean at rest */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                          <span style={{ fontSize: 8, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: pill.bg, color: pill.color, opacity: added ? 0.5 : 1 }}>{pill.label}</span>
                          {added && (
                            <span style={{ fontSize: 9, fontWeight: 600, color: "var(--qb-muted)" }} data-testid={`catalog-${p.name.replace(/\s/g, '-').toLowerCase()}-already`}>
                              Already in quote
                            </span>
                          )}
                        </div>
                        {/* Description — hidden by default, expands
                            on hover per mockup `.pd` rule. */}
                        {p.description && (
                          <div style={{
                            fontSize: 10.5, color: "var(--qb-muted)",
                            lineHeight: 1.4,
                            maxHeight: isHovered ? 52 : 0,
                            marginTop: isHovered ? 4 : 0,
                            overflow: "hidden",
                            transition: "max-height 0.16s ease, margin-top 0.16s ease",
                          }}>
                            {p.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {expanded && (
                    <button onClick={addCustomItem} style={{
                      display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", fontSize: 10, color: "var(--blue)", background: "none",
                      border: "0.5px dashed var(--border)", borderRadius: 6, cursor: "pointer", width: "100%", marginBottom: 4, fontFamily: "var(--font)"
                    }}>
                      <Plus style={{ width: 10, height: 10 }} /> Custom item
                    </button>
                  )}
                </div>
              );
            })}
            {/* Iter 331.2 — Gap 7: Browse all products entry point */}
            <button onClick={() => setBrowseProductsOpen(true)} className="btn" data-testid="qb-browse-all-btn" style={{ width: "100%", marginTop: 10, padding: "8px 10px", fontSize: 11, background: "transparent", border: "1px dashed var(--qb-line)", color: "var(--qb-navy)", fontWeight: 600, justifyContent: "center" }}>
              Browse all products →
            </button>
            {/* AI Recommendation */}
            {selectedLead?.pain_points && (
              <div style={{ marginTop: 8, padding: 10, background: "var(--blue-light)", borderRadius: 8, border: "0.5px solid rgba(0,113,227,0.15)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, color: "var(--blue)", marginBottom: 4 }}>
                  <Sparkles style={{ width: 10, height: 10 }} /> Claude recommends
                </div>
                <div style={{ fontSize: 9, color: "var(--text-secondary)", marginBottom: 6 }}>
                  Based on {selectedLead.contact_name}'s pain points
                </div>
                {(selectedLead.product_interest || []).slice(0, 2).map((p, i) => (
                  <div key={i} style={{ fontSize: 10, fontWeight: 500, color: "var(--text-primary)" }}>{p}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CENTER PANEL — Quote Builder (Iter 331 restyled with paper cards) */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 24px" }}>
          {/* Section 1: Contact & Lead — metadata strip */}
          <div className="qb-paper" style={{ padding: metadataCollapsed ? "12px 18px" : "16px 18px", marginBottom: 14 }} data-testid="qb-metadata-strip">
            {metadataCollapsed && (contactObj || selectedLead || ehiClient) ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }} data-testid="qb-metadata-collapsed">
                <div style={{ fontSize: 12, color: "var(--qb-ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  <span style={{ color: "var(--qb-muted)", fontWeight: 600 }}>Prepared for:</span>{" "}
                  {selectedLead && <><strong style={{ color: "var(--qb-ink)" }}>{selectedLead.company || selectedLead.title || "Lead"}</strong></>}
                  {isEhiRecipient && ehiClient && <><strong style={{ color: "var(--qb-ink)" }}>{ehiClient.clinic || "EHI client"}</strong> · EHI Client ID {ehiClient.client_id}</>}
                  {contactObj && <> · {contactObj.name || contactObj.email}</>}
                  {validUntil && !noExpiry && <> · Valid until {new Date(validUntil).toLocaleDateString()}</>}
                </div>
                <button onClick={() => setMetadataCollapsed(false)} className="btn" data-testid="qb-metadata-edit" style={{ padding: "4px 10px", fontSize: 10, background: "transparent", border: "1px solid var(--qb-line)", color: "var(--qb-navy)", fontWeight: 600 }}>
                  Edit
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--qb-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Recipient</div>
                    <RecipientTypeToggle value={recipientType} onChange={switchRecipientType} />
                  </div>
                  {(contactObj || selectedLead || ehiClient) && (
                    <button onClick={() => setMetadataCollapsed(true)} className="btn" data-testid="qb-metadata-collapse" style={{ padding: "3px 8px", fontSize: 10, background: "transparent", border: "1px solid var(--qb-line)", color: "var(--qb-muted)" }}>
                      Collapse
                    </button>
                  )}
                </div>
                {isEhiRecipient ? (
                  <div data-testid="qb-ehi-recipient">
                    <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 }}>EHI Client</label>
                    <EhiClientPicker token={token} value={ehiClient}
                      onPick={(c) => setEhiClient({ client_id: c.client_id, clinic: c.clinic || "", email: c.email || "", phone: c.phone || "", address: [c.address, c.state_code].filter(Boolean).join(", ") })}
                      onClear={() => setEhiClient(null)} />
                    {ehiClient && <EhiClientFields value={ehiClient} onChange={setEhiClient} />}
                  </div>
                ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 }}>Contact</label>
              <SearchableEntityPicker
                testid="quote-contact-picker"
                placeholder="Search contacts…"
                value={contactId}
                items={contacts.map(c => ({
                  id: c.id,
                  label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "Unnamed",
                  sub: [c.company || c.practice_name, c.email].filter(Boolean).join(" · "),
                  raw: c,
                }))}
                onChange={(id) => setContactId(id)}
                onCreateNew={(q) => navigate(`/contacts?new=1&name=${encodeURIComponent(q)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`)}
              />
              {selectedContact && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 8px", background: "var(--blue-light)", borderRadius: 6 }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--blue)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>
                    {(selectedContact.first_name?.[0] || "") + (selectedContact.last_name?.[0] || "")}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600 }}>{selectedContact.first_name} {selectedContact.last_name}</div>
                    <div style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{selectedContact.company}</div>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 }}>Lead / Opportunity</label>
              <SearchableEntityPicker
                testid="quote-lead-picker"
                placeholder="Search leads…"
                value={leadId}
                items={leads.map(l => ({
                  id: l.id,
                  label: l.title || `${l.first_name || ""} ${l.last_name || ""}`.trim() || l.company || "Unnamed lead",
                  sub: [l.company || l.practice_name, l.stage, l.contact_name].filter(Boolean).join(" · "),
                  raw: l,
                }))}
                onChange={(id) => setLeadId(id)}
                onCreateNew={(q) => navigate(`/add-lead?name=${encodeURIComponent(q)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`)}
              />
              {selectedLead && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 8px", background: "var(--amber-light)", borderRadius: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{selectedLead.title}</div>
                  <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: "var(--blue-light)", color: "var(--blue)", fontWeight: 600 }}>{selectedLead.stage}</span>
                </div>
              )}
            </div>
          </div>
                )}
              </>
            )}
          </div>

          {/* Section 1.5: EHI Programs (Pricing Rules, Phase 2) */}
          <div className="qb-paper" style={{ padding: "16px 18px", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 650, color: "var(--qb-ink)", letterSpacing: "-0.005em" }}>EHI Care-Coordination Programs</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["CCM", "RPM", "APCM"].map(p => {
                  const added = ehiPrograms.some(e => e.program === p);
                  return (
                    <button
                      key={p}
                      data-testid={`ehi-add-${p}`}
                      onClick={() => addEhiProgram(p)}
                      disabled={added}
                      style={{
                        fontSize: 10, padding: "4px 10px", borderRadius: 6,
                        background: added ? "var(--page-bg)" : "rgba(0,113,227,0.08)",
                        color: added ? "var(--text-tertiary)" : "#0071e3",
                        border: `0.5px solid ${added ? "var(--border)" : "rgba(0,113,227,0.25)"}`,
                        cursor: added ? "not-allowed" : "pointer",
                      }}>
                      {added ? `${p} added` : `+ ${p}`}
                    </button>
                  );
                })}
              </div>
            </div>
            <EHIProgramsPanel
              value={ehiPrograms}
              onChange={setEhiPrograms}
              onComputedChange={setEhiComputed}
            />
          </div>

          {/* Iter 188.46 · Phase 3 — Proposal Builder. Sits above
              Line items. Off by default → renders a one-line
              collapsed strip. Toggling "Include" expands to the
              full rich-text section editor + template picker. */}
          <ProposalBuilderPanel
            included={proposalIncluded}
            onIncludedChange={setProposalIncluded}
            sections={proposalSections}
            onSectionsChange={setProposalSections}
            quoteHasLineItems={lineItems.length > 0}
            lineItems={lineItems}
            terms={terms}
            authHeaders={{ Authorization: `Bearer ${token}` }}
          />

          {/* Section 2: Line Items */}
          <div className="qb-paper" style={{ padding: "16px 18px", marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 650, color: "var(--qb-ink)", marginBottom: 12, letterSpacing: "-0.005em" }}>Line items</div>
            {(lineItems.length === 0 && (ehiComputed?.line_items || []).length === 0) ? (
              <div style={{ padding: 32, textAlign: "center", border: "1.5px dashed var(--border)", borderRadius: 10, color: "var(--text-tertiary)" }}>
                <DollarSign style={{ width: 24, height: 24, margin: "0 auto 8px", opacity: 0.3 }} />
                <div style={{ fontSize: 12, fontWeight: 500 }}>No items added</div>
                <div style={{ fontSize: 10, marginTop: 4 }}>Select products from the left panel</div>
              </div>
            ) : (
              <div style={{ border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "20px 2fr 60px 100px 100px 30px", gap: 8, padding: "8px 12px", background: "var(--page-bg)", fontSize: 9, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3 }}>
                  <span></span><span>Product</span><span>Qty</span><span>Rate</span><span style={{ textAlign: "right" }}>Amount</span><span></span>
                </div>
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleLineItemDragEnd}
                >
                <SortableContext
                  items={lineItems.map((li) => li._lineId)}
                  strategy={verticalListSortingStrategy}
                >
                {buildRenderList(lineItems).map((entry, ri) => {
                  // Iter 188.44 · Phase 2-A — package-subtotal footer
                  // row emitted after a base subscription's last
                  // attached seat. Shows "N seats · <base> package
                  // subtotal $X/mo". Only monthly totals contribute;
                  // annual/one-time/rate are shown separately in the
                  // right rail (per spec §3).
                  if (entry.type === "package-subtotal") {
                    return (
                      <div
                        key={`sub-${entry.parentRow._lineId}`}
                        data-testid={`qb-package-subtotal-${entry.parentRow._lineId}`}
                        style={{
                          padding: "6px 12px 6px 40px",
                          borderTop: "0.5px solid var(--border)",
                          background: "var(--page-bg)",
                          fontSize: 10,
                          color: "var(--text-tertiary)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontWeight: 600,
                        }}
                      >
                        <span>{entry.seatCount} seat{entry.seatCount === 1 ? "" : "s"} · {entry.parentRow.product} package subtotal</span>
                        <span style={{ fontWeight: 700, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
                          ${entry.subtotalMonthly.toLocaleString(undefined, { maximumFractionDigits: 2 })}<span style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 500 }}>/mo</span>
                        </span>
                      </div>
                    );
                  }
                  const item = entry.row;
                  const i = entry.sourceIdx;
                  const isChild = !!entry.isChild;
                  const connector = entry.connector;
                  const isHeading = item?.kind === "heading";
                  const baseCount = getBases(lineItems).length;
                  const orphanSeat = item?.kind === "seat" && !item.parent && baseCount > 0;
                  return (
                  <SortableItemBox
                    key={item._lineId}
                    id={item._lineId}
                    testid={`line-item-${i}`}
                    style={{
                      padding: isChild ? "10px 12px 10px 32px" : "10px 12px",
                      borderTop: "0.5px solid var(--border)",
                      background: isHeading ? "var(--page-bg)" : "#ffffff",
                      position: "relative",
                    }}
                  >
                    {isChild && (
                      /* Iter 188.44 · Phase 2-A — indent connector ├/└
                         drawn as a small absolute-positioned glyph so
                         the grid layout below stays untouched. */
                      <span
                        aria-hidden
                        data-testid={`qb-seat-connector-${i}`}
                        style={{
                          position: "absolute",
                          left: 14,
                          top: 14,
                          fontSize: 12,
                          color: "var(--text-tertiary)",
                          fontFamily: "monospace",
                          userSelect: "none",
                        }}
                      >
                        {connector === "last" ? "└" : "├"}
                      </span>
                    )}
                    {orphanSeat && (
                      /* Iter 188.44 · Phase 2-A — amber "Attach to"
                         chooser for seats when 2+ base subscriptions
                         are on the quote. Per spec UX-02: rep must
                         explicitly pick which base a seat rides under
                         when the answer isn't obvious. */
                      <div
                        data-testid={`qb-seat-attach-${i}`}
                        style={{
                          marginBottom: 6,
                          padding: "6px 8px",
                          background: "#fef3c7",
                          border: "0.5px solid #fcd34d",
                          borderRadius: 6,
                          fontSize: 10,
                          color: "#92400e",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>Attach to:</span>
                        <select
                          value={item.parent || ""}
                          onChange={(e) => updateLineItem(i, "parent", e.target.value || null)}
                          className="input"
                          style={{ fontSize: 10, height: 22, flex: 1, background: "#fff" }}
                          data-testid={`qb-seat-attach-select-${i}`}
                        >
                          <option value="">— choose a base subscription —</option>
                          {getBases(lineItems).map((b) => (
                            <option key={b._lineId} value={b._lineId}>{b.product}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {isHeading ? (
                      /* Iter 188.44 · Phase 2-A — section-heading row.
                         Visual divider only; no qty/rate/amount fields
                         and no pricing math (total is 0). */
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "20px 1fr 30px",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <span
                          data-testid={`line-item-drag-${i}`}
                          title="Drag to reorder — the whole row is grabbable"
                          style={{ color: "var(--text-tertiary)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}
                          aria-hidden
                        >
                          <GripVertical style={{ width: 14, height: 14 }} />
                        </span>
                        <input
                          value={item.product === "Section heading" ? "" : item.product}
                          onChange={(e) => updateLineItem(i, "product", e.target.value || "Section heading")}
                          placeholder="Section heading (e.g. Care coordination, Setup fees)"
                          data-testid={`line-item-heading-title-${i}`}
                          className="input"
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "var(--qb-ink)",
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                            border: "none",
                            background: "transparent",
                            padding: "6px 0",
                            borderBottom: "1px solid var(--border)",
                          }}
                        />
                        <button
                          onClick={() => removeLineItem(i)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 2 }}
                          data-testid={`line-item-remove-${i}`}
                        >
                          <X style={{ width: 12, height: 12 }} />
                        </button>
                      </div>
                    ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "20px 2fr 60px 100px 100px 30px", gap: 8, alignItems: "center" }}>
                      <span
                        data-testid={`line-item-drag-${i}`}
                        title="Drag to reorder — the whole row is grabbable"
                        style={{ color: "var(--text-tertiary)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}
                        aria-hidden
                      >
                        <GripVertical style={{ width: 14, height: 14 }} />
                      </span>
                      <div>
                        {item.isCustom ? (
                          <>
                            <input
                              value={item.product}
                              onChange={e => updateLineItem(i, "product", e.target.value)}
                              placeholder="Product name"
                              data-testid={`line-item-custom-name-${i}`}
                              className="input" style={{ fontSize: 12, height: 28, marginBottom: 4 }}
                            />
                            {/* Iter 140.6 — custom items now expose a */}
                            {/* description field so reps can document */}
                            {/* what the bespoke line covers (was a */}
                            {/* read-only span before, hence empty). */}
                            <input
                              value={item.description || ""}
                              onChange={e => updateLineItem(i, "description", e.target.value)}
                              placeholder="Description (what this line covers)"
                              data-testid={`line-item-custom-desc-${i}`}
                              className="input" style={{ fontSize: 10, height: 24, color: "var(--text-secondary)" }}
                            />
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>{item.product}</div>
                            <div style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{item.description}</div>
                          </>
                        )}
                      </div>
                      <div data-testid={`line-item-qty-wrap-${i}`}>
                        {(() => {
                          // Iter 188.44 · Phase 2-A — kind-aware qty control.
                          //   base    → static "1", not editable
                          //   seat    → stepper with +/-
                          //   feature → checkbox (on = qty 1, off = remove)
                          //   default → numeric field (current behaviour)
                          const mode = qtyControlKind(item);
                          if (mode === "static") {
                            return (
                              <div
                                data-testid={`line-item-qty-${i}`}
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "var(--text-tertiary)",
                                  textAlign: "center",
                                  height: 28,
                                  lineHeight: "28px",
                                  cursor: "not-allowed",
                                }}
                                title="Base subscriptions are always quantity 1"
                              >
                                1
                              </div>
                            );
                          }
                          if (mode === "stepper") {
                            const q = Math.max(1, parseInt(item.quantity, 10) || 1);
                            return (
                              <div style={{ display: "flex", alignItems: "center", height: 28, border: "0.5px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
                                <button
                                  type="button"
                                  onClick={() => updateLineItem(i, "quantity", Math.max(1, q - 1))}
                                  data-testid={`line-item-qty-dec-${i}`}
                                  style={{ width: 18, height: 26, background: "transparent", border: "none", cursor: q > 1 ? "pointer" : "not-allowed", color: "var(--text-secondary)", fontSize: 12, lineHeight: 1 }}
                                  disabled={q <= 1}
                                >‹</button>
                                <div
                                  data-testid={`line-item-qty-${i}`}
                                  style={{ flex: 1, textAlign: "center", fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                                >{q}</div>
                                <button
                                  type="button"
                                  onClick={() => updateLineItem(i, "quantity", q + 1)}
                                  data-testid={`line-item-qty-inc-${i}`}
                                  style={{ width: 18, height: 26, background: "transparent", border: "none", cursor: "pointer", color: "var(--text-secondary)", fontSize: 12, lineHeight: 1 }}
                                >›</button>
                              </div>
                            );
                          }
                          if (mode === "checkbox") {
                            return (
                              <label
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 28, cursor: "pointer" }}
                                title="Toggle this feature on / off"
                              >
                                <input
                                  type="checkbox"
                                  checked={(parseInt(item.quantity, 10) || 0) > 0}
                                  onChange={(e) => updateLineItem(i, "quantity", e.target.checked ? 1 : 0)}
                                  data-testid={`line-item-qty-${i}`}
                                  style={{ width: 16, height: 16, cursor: "pointer" }}
                                />
                              </label>
                            );
                          }
                          return (
                            <input
                              type="number"
                              data-testid={`line-item-qty-${i}`}
                              // Iter 140.6 — strip leading zeros on render so
                              // typing "5" into an input that previously held
                              // "0" doesn't display as "05". The state value
                              // remains numeric for math; only the rendered
                              // input string is normalised.
                              value={String(item.quantity ?? "").replace(/^0+(?=\d)/, "")}
                              onChange={e => updateLineItem(i, "quantity", e.target.value)}
                              className="input" style={{ fontSize: 11, height: 28, textAlign: "center" }} min="1"
                            />
                          );
                        })()}
                      </div>
                      <div>
                        <input
                          type="number"
                          data-testid={`line-item-price-${i}`}
                          value={String(item.unit_price ?? "").replace(/^0+(?=\d)/, "")}
                          onChange={e => updateLineItem(i, "unit_price", e.target.value)}
                          className="input" style={{ fontSize: 11, height: 28 }} step="0.01"
                        />
                        {item.original_price && parseFloat(item.unit_price) !== item.original_price && (
                          <div style={{ fontSize: 8, color: "var(--text-tertiary)", textDecoration: "line-through" }}>
                            was {item.pricing_model === "percentage" ? `${item.original_price}${item.unit_label}` : `$${item.original_price}${item.unit_label}`}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }} data-testid={`line-item-amount-${i}`}>
                        {(() => {
                          // Iter 333 (Option A / FIX-02) — the amount cell
                          // is now cadence-aware. Percentage lines quote a
                          // rate ("6% of collections") in purple. Annual
                          // lines end in /yr; one-time in " one-time". This
                          // stops the "$6 product" confusion that Pro
                          // Bundle used to trigger.
                          const basis = item.basis || (item.pricing_model === "percentage"
                            ? (String(item.unit_label || "").toLowerCase().includes("reimb") ? "pctreimb" : "pctcoll")
                            : (String(item.unit_label || "").match(/\/yr/) ? "annual"
                              : String(item.unit_label || "").match(/one-time|onetime/) ? "onetime"
                              : "fixed"));
                          const unit = parseFloat(item.unit_price) || 0;
                          const total = parseFloat(item.total) || 0;
                          if (basis === "pctcoll" || basis === "pctreimb") {
                            return <span style={{ color: "#6d40c8" }}>{unit}% <span style={{ fontSize: 9, fontWeight: 500 }}>of {basis === "pctcoll" ? "collections" : "reimbursement"}</span></span>;
                          }
                          if (basis === "annual") return <>${total.toLocaleString()}<span style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 500 }}>/yr</span></>;
                          if (basis === "onetime") return <>${total.toLocaleString()}<span style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 500 }}> one-time</span></>;
                          return <>${total.toLocaleString()}<span style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 500 }}>/mo</span></>;
                        })()}
                      </div>
                      <button onClick={() => removeLineItem(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 2 }} data-testid={`line-item-remove-${i}`}>
                        <X style={{ width: 12, height: 12 }} />
                      </button>
                    </div>
                    )}
                    {/* Collections estimate for percentage items */}
                    {item.pricing_model === "percentage" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6, padding: "6px 8px", background: "#f3e8ff", borderRadius: 4, fontSize: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ color: "#7c3aed", fontWeight: 500 }}>Based on monthly collections:</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <span style={{ color: "#7c3aed" }}>$</span>
                            <input type="number" value={collectionsEstimate} onChange={e => setCollectionsEstimate(parseFloat(e.target.value) || 0)}
                              className="input" style={{ width: 100, height: 24, fontSize: 11 }} />
                            <span style={{ color: "#7c3aed" }}>/mo</span>
                          </div>
                        </div>
                        {/* Iter 140.3 — two separated lines per user spec: */}
                        {/* (1) the % estimate for this RCM item; (2) the    */}
                        {/* combined flat MRR across all non-% line items so */}
                        {/* reps see the full monthly picture at a glance.   */}
                        <div data-testid={`collections-breakdown-${i}`} style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#7c3aed" }}>
                            <span>{item.product} @ {parseFloat(item.unit_price) || 0}%</span>
                            <span style={{ fontWeight: 600 }}>${((collectionsEstimate * (parseFloat(item.unit_price) || 0) / 100)).toLocaleString()}/mo</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#7c3aed" }}>
                            <span>Flat monthly recurring (other line items)</span>
                            <span style={{ fontWeight: 600 }}>${flatMonthlyRecurring.toLocaleString()}/mo</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#7c3aed", borderTop: "0.5px solid rgba(124,58,237,0.3)", paddingTop: 2, marginTop: 1, fontWeight: 700 }}>
                            <span>Combined monthly est.</span>
                            <span>${(((collectionsEstimate * (parseFloat(item.unit_price) || 0) / 100)) + flatMonthlyRecurring).toLocaleString()}/mo</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Iter 140.6 — Discount line UI */}
                    {(item.pricing_model === "setup_discount" || item.pricing_model === "product_discount") && (() => {
                      const dStatus = getDiscountStatus(item);
                      const reduction = computeDiscountReduction(item, lineItems);
                      const statusTone = dStatus === "expired"
                        ? { bg: "#f3f4f6", fg: "#6b7280", label: "Expired — auto-deactivated" }
                        : dStatus === "warning"
                        ? { bg: "#fff4d6", fg: "#a16207", label: "Expires soon (within 7 days)" }
                        : { bg: "var(--green-light, #ddf6e8)", fg: "#0F6E56", label: "Active" };
                      const productOptions = lineItems
                        .filter(l => l !== item && l.pricing_model !== "setup_discount" && l.pricing_model !== "product_discount" && l.product)
                        .map(l => l.product);
                      return (
                        <div data-testid={`discount-config-${i}`} style={{ marginTop: 6, padding: "8px 10px", background: dStatus === "expired" ? "#f9fafb" : "var(--bg-secondary, #fafafa)", border: "0.5px dashed var(--border)", borderRadius: 6, fontSize: 11, opacity: dStatus === "expired" ? 0.55 : 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: statusTone.bg, color: statusTone.fg, fontWeight: 700, textTransform: "uppercase" }}>{statusTone.label}</span>
                            {reduction > 0 && <span style={{ color: "#0F6E56", fontWeight: 600 }}>−${reduction.toLocaleString()} off</span>}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: item.pricing_model === "product_discount" ? "1fr 80px 100px 88px 130px" : "1fr 80px 100px", gap: 6, alignItems: "center" }}>
                            <select
                              data-testid={`discount-type-${i}`}
                              value={item.discount_type || "percentage"}
                              onChange={e => updateLineItem(i, "discount_type", e.target.value)}
                              className="input" style={{ fontSize: 11, height: 26 }}
                            >
                              <option value="percentage">% off</option>
                              <option value="fixed">$ off (flat)</option>
                            </select>
                            <input
                              type="number"
                              data-testid={`discount-amount-${i}`}
                              value={String(item.discount_amount ?? "").replace(/^0+(?=\d)/, "")}
                              onChange={e => updateLineItem(i, "discount_amount", e.target.value)}
                              placeholder={item.discount_type === "fixed" ? "$ amount" : "% off"}
                              className="input" style={{ fontSize: 11, height: 26, textAlign: "right" }}
                              min="0" step="0.01"
                            />
                            {item.pricing_model === "product_discount" ? (
                              <>
                                <select
                                  data-testid={`discount-target-${i}`}
                                  value={item.target_product || ""}
                                  onChange={e => updateLineItem(i, "target_product", e.target.value)}
                                  className="input" style={{ fontSize: 11, height: 26 }}
                                >
                                  <option value="">Pick product…</option>
                                  {productOptions.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                                {/* Iter 177.4 (Iratzi #2f539f99 Feb 2026) —
                                    quick-pick discount duration 1-12 months.
                                    Setting this updates `expires_at` so the
                                    backend / quote PDF / customer email all
                                    show the same expiry the rep selected.
                                    The date picker is preserved alongside so
                                    Iratzi can fine-tune (e.g. "exactly end of
                                    Q3") instead of being forced into whole
                                    months. */}
                                <select
                                  data-testid={`discount-months-${i}`}
                                  value={item.expires_months || ""}
                                  onChange={e => {
                                    const m = e.target.value;
                                    if (!m) {
                                      updateLineItem(i, "expires_months", "");
                                      updateLineItem(i, "expires_at", "");
                                      return;
                                    }
                                    updateLineItem(i, "expires_months", m);
                                    const d = new Date();
                                    d.setMonth(d.getMonth() + parseInt(m, 10));
                                    updateLineItem(i, "expires_at", d.toISOString().slice(0, 10));
                                  }}
                                  title="Discount length (months)"
                                  className="input" style={{ fontSize: 11, height: 26 }}
                                >
                                  <option value="">Length…</option>
                                  {Array.from({ length: 12 }, (_, n) => n + 1).map(n => (
                                    <option key={n} value={n}>
                                      {n} {n === 1 ? "month" : "months"}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  type="date"
                                  data-testid={`discount-expires-${i}`}
                                  value={item.expires_at || ""}
                                  onChange={e => updateLineItem(i, "expires_at", e.target.value)}
                                  title="Discount expires on (pick date)"
                                  className="input" style={{ fontSize: 11, height: 26 }}
                                />
                              </>
                            ) : (
                              <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>applies to Setup Fee</span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </SortableItemBox>
                );
                })}
                </SortableContext>
                </DndContext>
                {/* Read-only EHI Care-Coordination program rows (CCM / RPM /
                    APCM). These are added / removed / adjusted in the top
                    "EHI Care-Coordination Programs" box; shown here (read-only)
                    so the Line items table reflects the full set of charges.
                    Percentage programs show the rate with no dollar; fixed /
                    per-unit show the amount. Mirrors the PDF / preview / detail
                    page rendering. */}
                {(ehiComputed?.line_items || []).map((li, i) => {
                  const prog = (li.program || "").trim();
                  let label = (li.label || prog).trim();
                  if (prog && label.toUpperCase().startsWith(prog.toUpperCase())) {
                    label = label.slice(prog.length).replace(/^[\s—–-]+/, "").trim() || (li.label || "");
                  }
                  const note = (li.note || "").trim();
                  const qty = li.qty;
                  const rate = li.rate;
                  const oneTime = Number(li.one_time) || 0;
                  const monthly = Number(li.monthly) || 0;
                  const annual = Number(li.annual) || 0;
                  const isPct = rate == null && qty == null && oneTime === 0 && monthly === 0 && annual === 0;
                  const money = (n) => `$${(Number(n) || 0).toLocaleString()}`;
                  let amount;
                  if (isPct) amount = <span style={{ color: "#6d40c8", fontWeight: 600 }}>{note || "50% of payer reimbursement"}</span>;
                  else if (oneTime > 0) amount = <>{money(oneTime)}<span style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 500 }}> one-time</span></>;
                  else if (monthly > 0) amount = <>{money(monthly)}<span style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 500 }}>/mo</span></>;
                  else if (annual > 0) amount = <>{money(annual)}<span style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 500 }}>/yr</span></>;
                  else amount = "—";
                  return (
                    <div
                      key={`ehi-line-${i}`}
                      data-testid={`qb-ehi-line-row-${i}`}
                      style={{ display: "grid", gridTemplateColumns: "20px 2fr 60px 100px 100px 30px", gap: 8, alignItems: "center", padding: "10px 12px", borderTop: "0.5px solid var(--border)", background: "var(--page-bg)" }}
                    >
                      <span aria-hidden />
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>{prog || "Program"}</span>
                          <span style={{ fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, padding: "1px 5px", borderRadius: 4, background: "rgba(109,64,200,0.1)", color: "#6d40c8" }}>Program</span>
                        </div>
                        <div style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{label}{!isPct && note ? ` · ${note}` : ""}</div>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)", textAlign: "center" }}>{(qty !== null && qty !== undefined && qty !== "") ? qty : ""}</div>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{rate != null ? `$${rate}` : ""}</div>
                      <div style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>{amount}</div>
                      <span aria-hidden title="Managed in the EHI Care-Coordination Programs box above" />
                    </div>
                  );
                })}
                {/* Iter 188.44 · Phase 2-A — inline actions row at the
                    bottom of the line-items table. "+ Custom item"
                    (existing) is kept in the catalog rail; here we
                    surface "+ Section heading" so reps can visually
                    group rows without adding a priced line. */}
                <div
                  data-testid="qb-line-item-actions"
                  style={{
                    display: "flex",
                    gap: 8,
                    padding: "8px 12px",
                    borderTop: "0.5px solid var(--border)",
                    background: "var(--page-bg)",
                  }}
                >
                  <button
                    type="button"
                    onClick={addCustomItem}
                    data-testid="qb-add-custom-item"
                    className="btn"
                    style={{
                      fontSize: 10,
                      padding: "4px 10px",
                      background: "transparent",
                      border: "0.5px dashed var(--border)",
                      color: "var(--blue)",
                      fontWeight: 600,
                    }}
                  >
                    <Plus style={{ width: 10, height: 10 }} /> Custom line item
                  </button>
                  <button
                    type="button"
                    onClick={addSectionHeading}
                    data-testid="qb-add-section-heading"
                    className="btn"
                    style={{
                      fontSize: 10,
                      padding: "4px 10px",
                      background: "transparent",
                      border: "0.5px dashed var(--border)",
                      color: "var(--qb-muted)",
                      fontWeight: 600,
                    }}
                  >
                    <Plus style={{ width: 10, height: 10 }} /> Section heading
                  </button>
                </div>
              </div>
            )}
            {/* Iter 333 (Option A) — center-panel running total upgraded
               to be cadence-aware. Shows the true recurring MRR
               (fixed + volume), NOT annual/one-time inflated. */}
            {lineItems.length > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <div style={{ width: 300 }} data-testid="qb-center-running-total">
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0" }}>
                    <span style={{ color: "var(--text-tertiary)" }}>Recurring monthly</span>
                    <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>${trueMonthlyMRR.toLocaleString(undefined, { maximumFractionDigits: 2 })}/mo</span>
                  </div>
                  {pricingBuckets.annual > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "3px 0", color: "var(--text-tertiary)" }}>
                      <span>Billed annually</span>
                      <span>${pricingBuckets.annual.toLocaleString()}/yr</span>
                    </div>
                  )}
                  {pricingBuckets.oneTime > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "3px 0", color: "var(--text-tertiary)" }}>
                      <span>One-time</span>
                      <span>${pricingBuckets.oneTime.toLocaleString()}</span>
                    </div>
                  )}
                  {discountReduction > 0 && (
                    <div data-testid="discount-summary-row" style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#0F6E56" }}>
                      <span>Discounts applied</span>
                      <span style={{ fontWeight: 600 }}>−${discountReduction.toLocaleString()}</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "6px 0 4px", borderTop: "0.5px solid var(--border)", marginTop: 4 }}>
                    <span style={{ color: "var(--text-tertiary)" }}>Recurring monthly total</span>
                    <span style={{ fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>${Math.max(0, trueMonthlyMRR - discountReduction).toLocaleString(undefined, { maximumFractionDigits: 2 })}/mo</span>
                  </div>
                  {showAnnualHeadline && trueMonthlyMRR > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "2px 0", color: "var(--text-tertiary)" }}>
                      <span>Annual estimate (recurring)</span>
                      <span>~${(Math.max(0, trueMonthlyMRR - discountReduction) * 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}/yr</span>
                    </div>
                  )}
                  {pricingBuckets.rateLines && pricingBuckets.rateLines.length > 0 && (
                    <div style={{ fontSize: 10, color: "#6d40c8", marginTop: 6, fontStyle: "italic", lineHeight: 1.5 }}>
                      Plus rate-based lines ({pricingBuckets.rateLines.map(r => `${r.rate}% of ${r.of}`).join(", ")}). Actual dollar depends on the client&rsquo;s monthly collections / reimbursement.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Notes & Terms */}
          <div className="qb-paper" style={{ padding: "16px 18px", marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 650, color: "var(--qb-ink)", marginBottom: 12, letterSpacing: "-0.005em" }}>Notes &amp; Terms</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: "var(--qb-muted)", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Notes (visible to client)</label>
                <QuoteRichTextField value={notes} onChange={setNotes} testid="quote-notes"
                  placeholder="Add a personalized message, implementation timeline, or special terms..." />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: "var(--qb-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Terms &amp; Conditions</label>
                  {termsAutoApplied && !userHasEditedTerms && (
                    <span data-testid="terms-autopopulated-badge" style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, padding: "1px 6px", borderRadius: 4, background: "rgba(16,185,129,0.12)", color: "#047857" }}>Auto-populated from categories</span>
                  )}
                  <button type="button" data-testid="reset-terms-btn" onClick={resetTermsToCategoryDefaults} style={{ fontSize: 10, fontWeight: 600, color: "var(--qb-accent, #6d40c8)", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>Reset to Category Defaults</button>
                </div>
                <QuoteRichTextField value={terms} onChange={handleTermsChange} testid="quote-terms" />
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3 }}>Valid until</label>
                <label style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, color: "var(--text-tertiary)", cursor: "pointer" }}>
                  <input type="checkbox" checked={noExpiry} onChange={e => setNoExpiry(e.target.checked)} /> No expiry
                </label>
              </div>
              {!noExpiry && <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} className="input" style={{ fontSize: 12, width: 180 }} data-testid="quote-expiry" />}
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 }}>
                Internal notes (not visible to client)
              </label>
              <QuoteRichTextField value={internalNotes} onChange={setInternalNotes} testid="quote-internal-notes"
                minHeight={60} accent placeholder="Your private notes about this deal..." />
            </div>
          </div>

          {/* Section 4: AI Summary */}
          {lineItems.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <button onClick={generateSummary} disabled={generatingSummary} className="btn" style={{
                fontSize: 11, padding: "8px 14px", background: "var(--blue-light)", color: "var(--blue)", border: "0.5px solid rgba(0,113,227,0.2)"
              }} data-testid="generate-summary-btn">
                {generatingSummary ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Sparkles style={{ width: 12, height: 12 }} />}
                Generate quote summary with Claude AI
              </button>
            </div>
          )}
        </div>

        {/* RIGHT PANEL — Live Preview & Totals (Iter 331 restyled as sticky summary panel) */}
        <div style={{ width: 320, flexShrink: 0, borderLeft: "1px solid var(--qb-line)", display: "flex", flexDirection: "column", background: "#fff" }}>
          <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--qb-muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Live preview</div>
            <LivePreview
              title={title} quoteNumber={quoteNumber || "QT-XXXX"}
              contact={previewContact} lineItems={lineItems}
              total={subtotal} notes={notes} terms={terms} validUntil={noExpiry ? null : validUntil}
              pricingBuckets={pricingBuckets} trueMonthlyMRR={trueMonthlyMRR}
            />
            {/* Iter 333 (Option A) — plain-English cadence-aware sentence.
               Blends monthly, annual, one-time, and rate lines correctly. */}
            {lineItems.length > 0 && (
              <div className="qb-paper" style={{ padding: "10px 12px", marginTop: 12 }}>
                <div className="qb-summary-sentence" data-testid="qb-summary-sentence">
                  {engineSentence}
                  {selectedLead && <> for <strong>{selectedLead.company || selectedLead.title || "the lead"}</strong></>} {isEhiRecipient && ehiClient && <> for <strong>{ehiClient.clinic || "the EHI client"}</strong></>}
                  {validUntil && !noExpiry && <> — valid until {new Date(validUntil).toLocaleDateString()}</>}
                </div>
              </div>
            )}
            {/* Iter 333 (Option A) — 4-bucket totals summary.
               NEVER combines monthly + annual + one-time. Rate lines
               surface separately in purple. Annual headline is
               suppressed when any rate line is present (spec §4). */}
            <div className="qb-paper" style={{ marginTop: 12, padding: "14px 16px" }} data-testid="qb-totals-panel">
              {/* Fixed monthly (headline) */}
              <div style={{ fontSize: 10, color: "var(--qb-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Fixed monthly</div>
              <div data-testid="qb-fixed-monthly" style={{ fontSize: 24, fontWeight: 800, color: "var(--qb-navy)", marginBottom: 10, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                ${(pricingBuckets.fixedMonthly || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                <span style={{ fontSize: 14, color: "var(--qb-muted)", fontWeight: 500 }}>/mo</span>
              </div>

              {/* Volume-based (estimated) — only when non-zero */}
              {pricingBuckets.volumeMonthly > 0 && (
                <>
                  <div style={{ fontSize: 10, color: "var(--qb-muted)", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Volume-based (est.)</div>
                  <div data-testid="qb-volume-monthly" style={{ fontSize: 15, fontWeight: 600, color: "var(--qb-ink-2)", marginBottom: 10, fontVariantNumeric: "tabular-nums" }}>
                    ${pricingBuckets.volumeMonthly.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    <span style={{ fontSize: 11, color: "var(--qb-muted)", fontWeight: 500 }}>/mo</span>
                  </div>
                </>
              )}

              {/* Billed annually — only when non-zero. NEVER added to monthly. */}
              {pricingBuckets.annual > 0 && (
                <>
                  <div style={{ fontSize: 10, color: "var(--qb-muted)", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Billed annually</div>
                  <div data-testid="qb-annual" style={{ fontSize: 15, fontWeight: 600, color: "var(--qb-ink-2)", marginBottom: 10, fontVariantNumeric: "tabular-nums" }}>
                    ${pricingBuckets.annual.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    <span style={{ fontSize: 11, color: "var(--qb-muted)", fontWeight: 500 }}>/yr</span>
                  </div>
                </>
              )}

              {/* One-time fees — only when non-zero. NEVER added to monthly. */}
              {pricingBuckets.oneTime > 0 && (
                <>
                  <div style={{ fontSize: 10, color: "var(--qb-muted)", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>One-time</div>
                  <div data-testid="qb-onetime" style={{ fontSize: 15, fontWeight: 600, color: "var(--qb-ink-2)", marginBottom: 10, fontVariantNumeric: "tabular-nums" }}>
                    ${pricingBuckets.oneTime.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    <span style={{ fontSize: 11, color: "var(--qb-muted)", fontWeight: 500 }}>one-time</span>
                  </div>
                </>
              )}

              {/* Rate lines — quoted as a rate, never a dollar (spec §4) */}
              {pricingBuckets.rateLines && pricingBuckets.rateLines.length > 0 && (
                <div data-testid="qb-rate-lines" style={{ marginBottom: 10, padding: "8px 10px", background: "#f5f0ff", border: "1px solid #e0d4ff", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: "#6d40c8", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Plus rate-based</div>
                  {pricingBuckets.rateLines.map((r, i) => (
                    <div key={i} data-testid={`qb-rate-line-${i}`} style={{ fontSize: 12, color: "#6d40c8", fontWeight: 600, lineHeight: 1.6 }}>
                      {r.rate}% of {r.of}
                      <span style={{ color: "#8b7cb8", fontWeight: 500, marginLeft: 6 }}>· {r.product}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Annual estimate — suppressed when rate lines exist (would be misleading) */}
              {showAnnualHeadline && trueMonthlyMRR > 0 && (
                <>
                  <div style={{ fontSize: 10, color: "var(--qb-muted)", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Annual estimate (recurring)</div>
                  <div data-testid="qb-annual-estimate" style={{ fontSize: 13, fontWeight: 600, color: "var(--qb-muted)", marginBottom: 10, fontVariantNumeric: "tabular-nums" }}>
                    ~${(trueMonthlyMRR * 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    <span style={{ fontSize: 11, fontWeight: 500 }}>/yr</span>
                  </div>
                </>
              )}

              <div style={{ borderTop: "1px solid var(--qb-line)", paddingTop: 8, fontSize: 10, color: "var(--qb-muted)", display: "flex", flexDirection: "column", gap: 3 }}>
                <div>Line items: {lineItems.length}</div>
                {validUntil && !noExpiry && <div>Valid until: {new Date(validUntil).toLocaleDateString()}</div>}
                {noExpiry && <div>No expiry date</div>}
              </div>
            </div>
          </div>
          {/* Sticky action buttons */}
          <div style={{ padding: 14, borderTop: "1px solid var(--qb-line)", background: "#fff", display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={() => handleSave(false)} disabled={saving} className="btn" style={{ width: "100%", fontSize: 11, padding: "9px", justifyContent: "center", background: "#fff", border: "1px solid var(--qb-line)", color: "var(--qb-ink-2)" }} data-testid="right-save-draft">
              <Save style={{ width: 12, height: 12 }} /> Save Draft
            </button>
            <button
              onClick={openAIEmailModal}
              disabled={saving || lineItems.length === 0 || (!contactId && !leadId)}
              className="btn"
              data-testid="right-ai-email-quote"
              style={{ width: "100%", fontSize: 11, padding: "9px", justifyContent: "center", background: "#EEEDFE", border: "1px solid #CECBF6", color: "#3C3489" }}>
              <Sparkles style={{ width: 12, height: 12 }} /> AI Draft Email
            </button>
            <button onClick={() => handleSave(true)} disabled={saving} className="btn qb-primary" style={{ width: "100%", fontSize: 12, padding: "10px", justifyContent: "center", fontWeight: 600 }} data-testid="right-send-quote">
              <Send style={{ width: 12, height: 12 }} /> Submit for Approval
            </button>
          </div>
        </div>
      </div>

      {/* ✦ AI-drafted Quote Email — review-before-send modal */}
      {emailModal && (
        <div data-testid="ai-email-modal" style={{ position: "fixed", inset: 0, background: "rgba(15, 41, 66, 0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "95%", maxWidth: 640, maxHeight: "90vh", overflow: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.25)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles style={{ width: 16, height: 16, color: "#3C3489" }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>Review AI-drafted email</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Edit anything before sending — the AI is just a starting point.</div>
                </div>
              </div>
              <button onClick={() => setEmailModal(null)} disabled={emailModal.sending} data-testid="ai-email-modal-close" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 4 }}>
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
              {emailModal.drafting ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 36, color: "#3C3489" }}>
                  <Loader2 style={{ width: 18, height: 18, animation: "spin 0.8s linear infinite", marginRight: 8 }} />
                  Drafting with Claude…
                </div>
              ) : (
                <>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 4 }}>To</label>
                    <input
                      value={emailModal.to}
                      onChange={e => setEmailModal(prev => ({ ...prev, to: e.target.value }))}
                      data-testid="ai-email-to"
                      className="input"
                      style={{ fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 4 }}>Subject</label>
                    <input
                      value={emailModal.subject}
                      onChange={e => setEmailModal(prev => ({ ...prev, subject: e.target.value }))}
                      data-testid="ai-email-subject"
                      className="input"
                      style={{ fontSize: 13, fontWeight: 600 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 4 }}>Message</label>
                    <textarea
                      value={emailModal.body}
                      onChange={e => setEmailModal(prev => ({ ...prev, body: e.target.value }))}
                      data-testid="ai-email-body"
                      rows={12}
                      className="input"
                      style={{ fontSize: 13, fontFamily: "var(--font)", lineHeight: 1.55, resize: "vertical", minHeight: 240 }} />
                  </div>
                  {emailModal.savedQuote?.quote_number && (
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)", background: "var(--page-bg)", padding: "6px 10px", borderRadius: 6 }}>
                      Quote <strong>{emailModal.savedQuote.quote_number}</strong> · ${(subtotal || 0).toLocaleString()}/mo · {lineItems.length} line item{lineItems.length === 1 ? "" : "s"}
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ padding: "12px 20px", borderTop: "0.5px solid var(--border)", display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
              <button
                onClick={regenerateAIEmail}
                disabled={emailModal.drafting || emailModal.sending}
                data-testid="ai-email-regenerate"
                className="btn"
                style={{ fontSize: 11, padding: "6px 12px", color: "#3C3489" }}>
                <Sparkles style={{ width: 12, height: 12 }} /> Re-draft with AI
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setEmailModal(null)}
                  disabled={emailModal.sending}
                  data-testid="ai-email-cancel"
                  className="btn"
                  style={{ fontSize: 11, padding: "6px 14px" }}>
                  Cancel
                </button>
                <button
                  onClick={sendAIEmail}
                  disabled={emailModal.drafting || emailModal.sending || !emailModal.subject?.trim() || !emailModal.body?.trim() || !emailModal.to?.trim()}
                  data-testid="ai-email-send"
                  className="btn btn-primary"
                  style={{ fontSize: 11, padding: "6px 16px" }}>
                  {emailModal.sending ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Send style={{ width: 12, height: 12 }} />}
                  {emailModal.sending ? "Sending…" : "Send Email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iter 331.2 — Sticky bottom summary bar (upgraded Iter 333) */}
      <div style={{ position: "sticky", bottom: 0, left: 0, right: 0, background: "#fff", borderTop: "1px solid var(--qb-line)", padding: "10px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, boxShadow: "0 -4px 12px -4px rgba(16,24,40,.08)", zIndex: 5 }} data-testid="qb-summary-bar">
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
          <div className="qb-summary-sentence" data-testid="qb-summary-bar-sentence" style={{ flex: 1, minWidth: 0 }}>
            {lineItems.length === 0 ? (
              <span className="qb-mut">No line items yet — add products from the catalog to build your quote.</span>
            ) : (
              <>
                {engineSentence}
                {selectedLead && <> · Prepared for <strong>{selectedLead.company || selectedLead.title || "the lead"}</strong></>} {isEhiRecipient && ehiClient && <> · Prepared for <strong>{ehiClient.clinic || "the EHI client"}</strong></>}
              </>
            )}
          </div>
          {trueMonthlyMRR > 10000 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 20, background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }} data-testid="qb-approval-flag" title="Approval required — recurring monthly exceeds $10k threshold">
              ⚑ Needs approval
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setPdfPreviewMode(true)}
            disabled={lineItems.length === 0}
            className="btn"
            style={{ fontSize: 11, padding: "8px 16px", background: "#fff", border: "1px solid var(--qb-line)", color: "var(--qb-ink-2)", opacity: lineItems.length === 0 ? 0.5 : 1 }}
            data-testid="qb-preview-pdf-btn"
            title={lineItems.length === 0 ? "Add at least one line item first" : "Open the on-page PDF preview — use Download for the file"}>
            <Eye style={{ width: 12, height: 12 }} /> Preview / Print PDF
          </button>
          <button onClick={() => handleSave(true)} disabled={saving} className="btn qb-primary" style={{ fontSize: 12, padding: "9px 20px", fontWeight: 600 }} data-testid="qb-summary-submit-btn">
            <Send style={{ width: 12, height: 12 }} /> Submit for approval
          </button>
        </div>
      </div>

      {/* Iter 331.2 — Gap 7: Browse all products 3-column modal */}
      {browseProductsOpen && (
        <div onClick={() => setBrowseProductsOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }} data-testid="qb-browse-products-backdrop">
          <div onClick={(e) => e.stopPropagation()} className="qb-paper" style={{ width: "min(1100px, 96vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", padding: 0 }} data-testid="qb-browse-products-modal">
            <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--qb-line)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--qb-ink)", letterSpacing: "-0.01em" }}>Browse all products</div>
                <div style={{ fontSize: 11, color: "var(--qb-muted)", marginTop: 2 }}>Full catalog · {ACTIVE_CATALOG.length} products</div>
              </div>
              <button onClick={() => setBrowseProductsOpen(false)} className="btn" style={{ padding: "6px 10px", fontSize: 11, background: "transparent", border: "1px solid var(--qb-line)" }} data-testid="qb-browse-close"><X style={{ width: 12, height: 12 }} /> Close</button>
            </div>
            <div style={{ padding: "12px 22px", borderBottom: "1px solid var(--qb-line)" }}>
              <input value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)} placeholder="Search products, categories, or CPT codes..." className="input" style={{ fontSize: 12 }} data-testid="qb-browse-search" />
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 20, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              {ACTIVE_CATALOG.filter(p => !catalogSearch || (p.name + " " + (p.description || "") + " " + (p.category || "")).toLowerCase().includes(catalogSearch.toLowerCase())).map(p => (
                <div key={p.id || p.name} className="qb-paper" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }} data-testid={`qb-browse-item-${p.id || p.name}`}>
                  <div style={{ fontSize: 12, fontWeight: 650, color: "var(--qb-ink)" }}>{p.name}</div>
                  {p.category && <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--qb-muted)", fontWeight: 600 }}>{p.category}</div>}
                  {p.description && <div style={{ fontSize: 11, color: "var(--qb-muted)", lineHeight: 1.4 }}>{p.description}</div>}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--qb-navy)", fontVariantNumeric: "tabular-nums" }}>{p.price != null ? `$${Number(p.price).toLocaleString()}` : "—"}{p.unit && <span style={{ fontSize: 10, color: "var(--qb-muted)", fontWeight: 500 }}>{p.unit}</span>}</div>
                    <button onClick={() => { addFromCatalog(p); setBrowseProductsOpen(false); }} className="btn qb-primary" style={{ fontSize: 10, padding: "5px 10px" }} data-testid={`qb-browse-add-${p.id || p.name}`}>Add +</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Iter 186 (Feb 2026) — real PDF preview overlay ported from
          /public/_mockup_ref.html. Kill-switch env
          REACT_APP_QUOTE_PDF_PREVIEW_V2=false falls back to the old
          placeholder overlay for instant rollback. */}
      {pdfPreviewMode && (process.env.REACT_APP_QUOTE_PDF_PREVIEW_V2 !== "false") && (
        <QuotePdfPreview
          quoteNumber={quoteNumber || "QT-XXXX"}
          quoteTitle={title || "Quote"}
          proposalMode={proposalIncluded}
          proposalSections={proposalSections}
          preparedFor={isEhiRecipient && ehiClient ? {
            org: ehiClient.clinic || "", name: "", addr: ehiClient.address || "", mail: ehiClient.email || "",
            phone: ehiClient.phone || "", clientId: ehiClient.client_id,
          } : {
            org: selectedContact?.company || selectedLead?.company || "",
            name: selectedContact
              ? `${selectedContact.first_name || ""} ${selectedContact.last_name || ""}`.trim()
              : (selectedLead?.contact_name || ""),
            addr: selectedContact?.address || selectedLead?.address || "",
            mail: selectedContact?.email || selectedLead?.email || "",
          }}
          preparedBy={{
            org: "Enable Healthcare Inc.",
            name: quoteOwner?.name || user?.full_name || user?.email || "",
            addr: "225 Brae Blvd, Suite 125 Park Ridge, NJ 07656",
            mail: quoteOwner?.email || user?.email || "",
          }}
          lineItems={lineItems}
          ehiLineItems={ehiComputed?.line_items || []}
          totals={{
            fixed: pricingBuckets.fixedMonthly || 0,
            variable: (pricingBuckets.volumeMonthly || 0) + (ehiComputed?.totals?.monthly_recurring || 0),
            monthly_recurring: trueMonthlyMRR + (ehiComputed?.totals?.monthly_recurring || 0),
            annual: pricingBuckets.annual || 0,
            one_time: (pricingBuckets.oneTime || 0) + (ehiComputed?.totals?.one_time || 0),
          }}
          notes={notes}
          terms={terms}
          validUntil={noExpiry ? null : validUntil}
          onClose={() => setPdfPreviewMode(false)}
        />
      )}
      {pdfPreviewMode && (process.env.REACT_APP_QUOTE_PDF_PREVIEW_V2 === "false") && (
        <div style={{ position: "fixed", inset: "52px 0 0 0", background: "rgba(15,23,42,0.45)", zIndex: 90, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px", overflow: "auto" }} data-testid="qb-pdf-preview-overlay">
          <div className="qb-paper" style={{ width: "min(800px, 96vw)", minHeight: "600px", padding: 0, overflow: "hidden", position: "relative" }}>
            <div style={{ background: "var(--qb-navy)", height: 80, display: "flex", alignItems: "center", padding: "0 32px", color: "#fff" }}>
              <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>Enable Healthcare</div>
              <div style={{ marginLeft: "auto", fontSize: 11, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>Quote {quoteNumber || "QT-XXXX"}</div>
            </div>
            <div style={{ padding: "40px 32px", textAlign: "center", color: "var(--qb-muted)" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--qb-ink)", marginBottom: 8 }}>PDF export template</div>
              <div style={{ fontSize: 12, maxWidth: 480, margin: "0 auto", lineHeight: 1.6 }}>Full PDF rendering with repeating letterhead, page numbers, and pricing table is coming in a future update. This preview is a placeholder.</div>
              <button onClick={() => setPdfPreviewMode(false)} className="btn qb-primary" style={{ fontSize: 12, padding: "9px 20px", marginTop: 20 }} data-testid="qb-pdf-preview-close">Back to builder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
